export interface LocalizedText {
  ru: string;
  uz: string;
  en: string;
}

export interface LayingScheme {
  schemeImage: string;  // URL картинки схемы (справа)
  caption: string | LocalizedText;  // мультиязычная подпись схемы
}

export interface LayingGalleryEntry {
  type: string;              // 'Палуба' | 'Ёлочка' | 'Плитка'
  label?: LocalizedText;     // мультиязычная подпись вида укладки
  mainImage: string;         // основная фотография (слева, первое фото галереи)
  galleryImages?: string[];  // все фото галереи укладки (до 8, слева на странице)
  schemes: LayingScheme[];
}

// SPC PANELS specific: Click System block
export interface ClickSystemBlock {
  title: string;       // заголовок (ru)
  image: string;       // URL изображения
  description: string; // описание (ru)
}

// SPC PANELS specific: Profiles Usage block
export interface ProfilesUsageBlock {
  image: string;       // URL изображения
  caption: string;     // подпись / описание (ru)
}

// ── SPC PANELS Palette ──────────────────────────────────────────────────────────
// Значения палитры для стеновых панелей — свои, независимые от SPC LAMINATE.
export const SPC_PANELS_PALETTE_LIST = [
  'БЕЛЫЙ',
  'СВЕТЛЫЙ',
  'СРЕДНИЙ',
  'СЕРЫЙ',
  'ТЁМНЫЙ',
  'КОРИЧНЕВЫЙ',
  'СИНЕ-СЕРЫЙ',
] as const;

export type SPCPanelsPalette = typeof SPC_PANELS_PALETTE_LIST[number];

// Маппинг для публичного фильтра / отображения
export const SPC_PANELS_PALETTE_LABELS: Record<string, { ru: string; en: string; uz: string }> = {
  'БЕЛЫЙ':      { ru: 'Белый',      en: 'White',        uz: 'Oq' },
  'СВЕТЛЫЙ':    { ru: 'Светлый',    en: 'Light',        uz: 'Och' },
  'СРЕДНИЙ':    { ru: 'Средний',    en: 'Medium',       uz: "O'rta" },
  'СЕРЫЙ':      { ru: 'Серый',      en: 'Grey',         uz: 'Kulrang' },
  'ТЁМНЫЙ':    { ru: 'Тёмный',     en: 'Dark',         uz: "To'q" },
  'КОРИЧНЕВЫЙ': { ru: 'Коричневый', en: 'Brown',        uz: 'Jigarrang' },
  'СИНЕ-СЕРЫЙ': { ru: 'Сине-серый', en: 'Blue-grey',    uz: 'Ko\'k-kulrang' },
};

// ── SPC PANELS Assembly Preset ─────────────────────────────────────────────────
// Общие данные сборки для ВСЕЙ серии SPC PANELS (не редактируется индивидуально).
export interface SPCPanelsAssemblyProfile {
  image: string;
  title: LocalizedText;
  description: LocalizedText;
}

export interface SPCPanelsAssemblyPreset {
  clickSystem: {
    title: LocalizedText;
    image: string;
    description: LocalizedText;
  };
  profiles: {
    title: LocalizedText;
    items: SPCPanelsAssemblyProfile[];
  };
}

export const SPC_PANELS_ASSEMBLY_PRESET: SPCPanelsAssemblyPreset = {
  clickSystem: {
    title: {
      ru: 'Система Click',
      en: 'Click System',
      uz: 'Click tizimi',
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2F4D27prRWgXT0iE6QnEvzCmWwkqw1%2Flaminat_animation_2_transparent__f838f554.webp?alt=media&token=56358ebb-7836-4a03-bc2c-8bd7bc40e074',
    description: {
      ru: 'Запатентованная система соединения Click обеспечивает плотный и надёжный монтаж стеновых панелей без клея и инструментов. Панели защёлкиваются с характерным звуком — это подтверждает правильность установки.',
      en: 'The patented Click connection system provides a tight and reliable installation of wall panels without adhesive or tools. Panels snap together with a characteristic sound — confirming correct installation.',
      uz: "Patentlangan Click ulash tizimi devor panellarini yelim va asboblarsiz mustahkam o'rnatishni ta'minlaydi. Panellar xarakterli ovoz bilan birlashadi — bu to'g'ri o'rnatilganligini tasdiqlaydi.",
    },
  },
  profiles: {
    title: {
      ru: 'Использование профилей',
      en: 'Profile Usage',
      uz: "Profillardan foydalanish",
    },
    items: [
      {
        image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2F4D27prRWgXT0iE6QnEvzCmWwkqw1%2F--__930d6841.webp?alt=media&token=adfff663-32e5-4114-8aef-94952a25169c',
        title: {
          ru: 'Внешний угловой профиль',
          en: 'External Corner Profile',
          uz: 'Tashqi burchak profili',
        },
        description: {
          ru: 'Используется для оформления внешних углов и торцов — придаёт завершённый вид и защищает кромки панелей от механических повреждений.',
          en: 'Used for finishing external corners and edges — gives a neat look and protects panel edges from mechanical damage.',
          uz: "Tashqi burchaklar va qirralarni bezatish uchun ishlatiladi — tartibli ko'rinish beradi va panel qirralarini mexanik shikastlanishdan himoya qiladi.",
        },
      },
      {
        image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2F4D27prRWgXT0iE6QnEvzCmWwkqw1%2F--__39f2ad13.webp?alt=media&token=a502f385-ca79-4073-887c-3f06d1576d04',
        title: {
          ru: 'Внутренний угловой профиль',
          en: 'Internal Corner Profile',
          uz: 'Ichki burchak profili',
        },
        description: {
          ru: 'Применяется при соединении панелей во внутренних углах — обеспечивает плотное примыкание и аккуратный стык без зазоров.',
          en: 'Used when connecting panels in internal corners — ensures tight fitting and a neat joint without gaps.',
          uz: "Ichki burchaklarda panellarni ulashda qo'llaniladi — bo'shliqsiz yaxshi birlashishni ta'minlaydi.",
        },
      },
      {
        image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2F4D27prRWgXT0iE6QnEvzCmWwkqw1%2F-__27a37feb.webp?alt=media&token=5bda173a-c974-46d7-8aa1-03465848238d',
        title: {
          ru: 'Финишный профиль',
          en: 'Finishing Profile',
          uz: 'Yakunlovchi profil',
        },
        description: {
          ru: 'Завершает монтаж по периметру, закрывает зазоры у пола, потолка и смежных поверхностей. Придаёт установке профессиональный вид.',
          en: 'Finishes the installation around the perimeter, covers gaps at the floor, ceiling and adjacent surfaces. Gives the installation a professional look.',
          uz: "O'rnatishni perimetr bo'ylab yakunlaydi, pol, ship va qo'shni yuzalar yonidagi bo'shliqlarni yopadi. O'rnatishga professional ko'rinish beradi.",
        },
      },
    ],
  },
};

export interface Product {
  id: string;
  name: string | LocalizedText;
  slug: string;
  series: string;
  collection?: string;
  code: string;
  palette?: string;
  groupType?: 'WD' | 'STP';
  colorTag?: string;
  installationType?: string;
  layingTypes?: string[];
  layingGallery?: LayingGalleryEntry[];  // галерея укладки из админки
  sizesTile?: string[];
  shortDescription?: string | LocalizedText;
  fullDescription?: string | LocalizedText;
  specs: {
    [key: string]: string;
  };
  image: string;
  images: string[];
  // Internal gallery: SPC PANELS + CARPET TILE (up to 8 images for product page thumbnails)
  internalImages?: string[];
  pdfUrl?: string;           // legacy
  pdfTechInfo?: string;      // URL техинформации (PDF)
  pdfCertificate?: string;   // URL сертификата (PDF)
  downloadables?: { name: string; url: string }[];
  compatibility?: string[];
  // SPC PANELS only
  clickSystem?: ClickSystemBlock;
  profilesUsage?: ProfilesUsageBlock;
}

export interface FormattedSpec {
  labelKey: string;
  value: string;
}

export const formatSpecValue = (val: unknown, t: (key: string) => string): string => {
  // Type guard: ensure we always work with a string
  let safeVal: string;
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') {
    // Could be LocalizedText — try to extract 'ru' field or stringify
    const obj = val as Record<string, unknown>;
    safeVal = (typeof obj.ru === 'string' ? obj.ru : String(val)) || '';
  } else {
    safeVal = String(val);
  }
  if (!safeVal) return '';

  const mm = t('product.unit.mm');
  const cm = t('product.unit.cm');
  const sep = t('product.decimal_separator');

  let result = safeVal;
  // Handle decimal separator: convert any comma or dot to the localized one
  // but only if it's between digits to avoid breaking other strings
  if (sep === ',') {
    result = result.replace(/(\d)\.(\d)/g, '$1,$2');
  } else {
    result = result.replace(/(\d),(\d)/g, '$1.$2');
  }
  
  // Replace units
  result = result.replace(/\s*(mm|мм)/gi, ` ${mm}`);
  result = result.replace(/\s*(cm|см)/gi, ` ${cm}`);
  
  return result;
};

export const getCardSpecs = (product: Product, language: string, t: (key: string) => string): FormattedSpec[] => {
  if (product.series === 'SPC LAMINATE') {
    return [
      { labelKey: 'product.spec.thickness', value: formatSpecValue(product.specs.thickness, t) },
      { labelKey: 'product.spec.lockType', value: product.specs.lockType || 'Uniclic' },
      { labelKey: 'product.spec.underlay', value: product.specs.underlay || 'IXPE' }
    ];
  }

  if (product.series === 'SPC PANELS') {
    return [
      { labelKey: 'product.spec.thickness', value: formatSpecValue(product.specs.thickness, t) },
      { labelKey: 'product.spec.width', value: formatSpecValue(product.specs.width, t) },
      { labelKey: 'product.spec.height', value: formatSpecValue(product.specs.height, t) }
    ];
  }

  if (product.series === 'CARPET TILE') {
    return [
      { labelKey: 'product.spec.totalHeight', value: formatSpecValue(product.specs.totalHeight || '6.2 mm', t) },
      { labelKey: 'product.spec.size', value: formatSpecValue(product.specs.size || '50x50 cm', t) },
      { labelKey: 'product.spec.pileHeight', value: formatSpecValue(product.specs.pileHeight || '3 mm', t) }
    ];
  }

  // Accessories or fallback
  const fallbackSpecs: FormattedSpec[] = [];
  Object.keys(product.specs).forEach(key => {
    fallbackSpecs.push({ 
      labelKey: `product.spec.${key}`, 
      value: formatSpecValue(product.specs[key], t) 
    });
  });
  
  // Return first 3 if possible
  return fallbackSpecs.slice(0, 3);
};

export const products: Product[] = [
  // SPC LAMINATE - 45 Products
  {
    id: 'spc-1',
    name: 'Drake Oak',
    slug: 'drake-oak-wh-001',
    series: 'SPC LAMINATE',
    code: 'WH-001',
    palette: 'WHITE',
    layingTypes: ['layout_plank', 'layout_herringbone'],
    shortDescription: {
      ru: 'Светлый дуб с мягкой текстурой — визуально расширяет пространство.',
      en: 'Light oak with a soft texture — visually expands the space.',
      uz: 'Yumshoq teksturali och dub — makonni vizual ravishda kengaytiradi.'
    },
    fullDescription: {
      ru: 'Drake Oak — спокойный светлый декор для современных интерьеров. Белая палитра делает помещение легче и «чище», хорошо сочетается с тёплым текстилем и натуральной мебелью. Подходит для дома и зон с повседневной нагрузкой, где важны аккуратный вид и простой уход.',
      en: 'Drake Oak is a calm light decor for modern interiors. The white palette makes the room lighter and "cleaner", pairing well with warm textiles and natural furniture. Suitable for homes and areas with daily loads where a neat appearance and easy maintenance are important.',
      uz: 'Drake Oak — zamonaviy interyerlar uchun sokin och dekor. Oq palitra xonani yanada yorug‘ va “toza” qiladi, iliq to‘qimachilik va tabiiy mebellar bilan yaxshi uyg‘unlashadi. Uy va kundalik yuklamali zonalar uchun mos keladi, u yerda tartibli ko‘rinish va oson parvarish muhim.'
    },
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FvPHrVFs1L1amA5l2PQVD9I1kXew1%2FDrakeOak01__9380da16.png?alt=media&token=e1dad96b-3bf6-413e-a0e0-654d2ea7e37d',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FvPHrVFs1L1amA5l2PQVD9I1kXew1%2FDrakeOak02__6ad14a06.png?alt=media&token=24718c6c-484a-4271-9f6e-4d0aab2e1ea6',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FvPHrVFs1L1amA5l2PQVD9I1kXew1%2FDrakeOak03__0beb5573.png?alt=media&token=831fb6be-6c0a-4343-98c2-71c00e7965b8',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FvPHrVFs1L1amA5l2PQVD9I1kXew1%2FDrakeOak04__ee909452.png?alt=media&token=1ac3c884-4542-46c7-86c0-487a9de1a2fe'
    ]
  },
  {
    id: 'spc-2',
    name: 'Hobart Oak',
    slug: 'hobart-oak-gr-001',
    series: 'SPC LAMINATE',
    code: 'GR-001',
    palette: 'GREY',
    layingTypes: ['layout_plank', 'layout_herringbone'],
    shortDescription: {
      ru: 'Серый дуб с нейтральным характером — база под любой стиль.',
      en: 'Grey oak with a neutral character — a base for any style.',
      uz: 'Neytral xarakterli kulrang dub — har qanday uslub uchun asos.'
    },
    fullDescription: {
      ru: 'Hobart Oak работает как универсальный фон: не спорит с мебелью и подчёркивает геометрию пространства. Серый оттенок хорошо поддерживает минимализм, лофт и современную классику. Практичный выбор для активных зон.',
      en: 'Hobart Oak works as a universal background: it does not clash with furniture and emphasizes the geometry of the space. The grey shade supports minimalism, loft, and modern classics. A practical choice for active zones.',
      uz: 'Hobart Oak universal fon sifatida ishlaydi: mebel bilan ziddiyatga kirmaydi va makon geometriyasini ta’kidlaydi. Kulrang soya minimalizm, loft va zamonaviy klassikani yaxshi qo‘llab-quvvatlaydi. Faol zonalar uchun amaliy tanlov.'
    },
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2F7fA6t0FVT2dql5bWqYILtlCZjB82%2FHobartOak01__fcee682f.png?alt=media&token=fc85ff0a-3569-4183-9f4c-b304c90b7264',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2F7fA6t0FVT2dql5bWqYILtlCZjB82%2FHobartOak02__f02a613c.png?alt=media&token=a3415be2-e6cf-469a-a99a-7242d7627914',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2F7fA6t0FVT2dql5bWqYILtlCZjB82%2FHobartOak03__15dfd3fd.png?alt=media&token=eac91283-5c95-4293-8507-42bda24c4189',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2F7fA6t0FVT2dql5bWqYILtlCZjB82%2FHobartOak04__06c8b51b.png?alt=media&token=d84ca7eb-07c8-425f-aa65-1e4347ed2d0c'
    ]
  },
  {
    id: 'spc-3',
    name: 'Pondosa Oak',
    slug: 'pondosa-oak-nl-001',
    series: 'SPC LAMINATE',
    code: 'NL-001',
    palette: 'NATURE LIGHT',
    layingTypes: ['layout_plank', 'layout_herringbone'],
    shortDescription: {
      ru: 'Тёплый светлый дуб — уют без лишней строгости.',
      en: 'Warm light oak — coziness without extra strictness.',
      uz: 'Iliq och dub — ortiqcha qat’iyatliliksiz qulaylik.'
    },
    fullDescription: {
      ru: 'Pondosa Oak добавляет интерьеру мягкое тепло и ощущение натуральности. Светлая природная палитра делает комнату дружелюбной и комфортной — особенно в жилых пространствах. Хорошо сочетается с бежевыми, молочными и оливковыми акцентами.',
      en: 'Pondosa Oak adds soft warmth and a sense of naturalness to the interior. The light natural palette makes the room friendly and comfortable — especially in residential spaces. Pairs well with beige, milky, and olive accents.',
      uz: 'Pondosa Oak interyerga yumshoq iliqlik va tabiiylik hissini qo‘shadi. Och tabiiy palitra xonani do‘stona va qulay qiladi — ayniqsa yashash maydonlarida. Bej, sut rang va zaytun aksentlari bilan yaxshi uyg‘unlashadi.'
    },
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FPondosaOak01__dc07bc7b.png?alt=media&token=1f8d9e56-3ad3-4598-8083-75ad0f1c1723',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FPondosaOak02__5d0ca33e.png?alt=media&token=ce7e0161-9c55-4d74-8959-4c21e86609c1',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FPondosaOak03__5b97d000.png?alt=media&token=ae969d21-72fd-44b1-8773-5026f6085995'
    ]
  },
  {
    id: 'spc-4',
    name: 'Paro Oak',
    slug: 'paro-oak-nm-001',
    series: 'SPC LAMINATE',
    code: 'NM-001',
    palette: 'NATURE MEDIUM',
    layingTypes: ['layout_plank', 'layout_herringbone'],
    shortDescription: {
      ru: 'Сбалансированный дуб средней тональности — спокойная «дорогая» подача.',
      en: 'Balanced medium-tone oak — a calm "expensive" presentation.',
      uz: 'Muvozanatli o‘rtacha tonli dub — sokin “qimmat” ko‘rinish.'
    },
    fullDescription: {
      ru: 'Paro Oak — уверенный средний оттенок, который выглядит собранно и современно. Он поддерживает интерьер с тёплым освещением и деревянной мебелью, не утяжеляя помещение. Подходит для гостиных, кабинетов и коммерческих пространств.',
      en: 'Paro Oak is a confident medium shade that looks composed and modern. It supports interiors with warm lighting and wooden furniture without weighting down the room. Suitable for living rooms, offices, and commercial spaces.',
      uz: 'Paro Oak — o‘ziga ishongan o‘rtacha soya, u yig‘ilgan va zamonaviy ko‘rinadi. U iliq yoritish va yog‘och mebelli interyerlarni qo‘llab-quvvatlaydi, xonani og‘irlashtirmaydi. Yashash xonalari, kabinetlar va tijorat maydonlari uchun mos.'
    },
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2F01__92cd548a.png?alt=media&token=784eef91-eb60-4932-8ca4-fed667a250c7',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FParoOak02__53946799.png?alt=media&token=20f1b212-4fea-424f-bbb1-cfeb6bdea316',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FParoOak03__41e90cd1.png?alt=media&token=cfb84578-2063-444b-b201-d0084b08c8a9'
    ]
  },
  {
    id: 'spc-5',
    name: 'Highland Oak',
    slug: 'highland-oak-nd-001',
    series: 'SPC LAMINATE',
    code: 'ND-001',
    palette: 'NATURE DARK',
    layingTypes: ['layout_plank', 'layout_herringbone'],
    shortDescription: {
      ru: 'Глубокий тёмный дуб — выразительный акцент и статусный тон.',
      en: 'Deep dark oak — an expressive accent and status tone.',
      uz: 'Chuqur to‘q dub — ifodali aksent va statusli ohang.'
    },
    fullDescription: {
      ru: 'Highland Oak делает интерьер контрастным и более «премиальным» по ощущению. Тёмная природная палитра красиво работает с металлом, камнем и насыщенными цветами. Хорош для зон, где нужен сильный характер.',
      en: 'Highland Oak makes the interior contrasting and more "premium" in feel. The dark natural palette works beautifully with metal, stone, and rich colors. Good for areas that need a strong character.',
      uz: 'Highland Oak interyerni kontrastli va his-tuyg‘u bo‘yicha yanada "premium" qiladi. To‘q tabiiy palitra metall, tosh va to‘yingan ranglar bilan chiroyli ishlaydi. Kuchli xarakter talab qilinadigan zonalar uchun yaxshi.'
    },
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2F7fA6t0FVT2dql5bWqYILtlCZjB82%2FHighlandOak01__56c96be3.png?alt=media&token=6eda4f25-d863-424f-a320-f365ca31dbcd',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2F7fA6t0FVT2dql5bWqYILtlCZjB82%2F02__fbdc2d96.png?alt=media&token=d8b433fe-a595-486f-b79f-8239c56089c4',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2F7fA6t0FVT2dql5bWqYILtlCZjB82%2F03__2a52f0e2.png?alt=media&token=79c583e3-cb74-422e-b3d7-e62d9d668372'
    ]
  },
  {
    id: 'spc-6',
    name: 'Merin Oak',
    slug: 'merin-oak-gr-002',
    series: 'SPC LAMINATE',
    code: 'GR-002',
    palette: 'GREY',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Холодный серый дуб — чистая современная эстетика.',
    fullDescription: 'Merin Oak подчёркивает современную архитектуру и ровные линии интерьера. Серый оттенок помогает собрать пространство и выглядит аккуратно в лаконичном дизайне. Хорошо сочетается с белыми стенами, чёрными деталями и стеклом.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FiH2ZUlBC2JYCy5uWxES7CI3Z1y32%2FMerinOak01__9891b689.png?alt=media&token=753eb861-16fe-4cb7-ba49-a6f055382904',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FiH2ZUlBC2JYCy5uWxES7CI3Z1y32%2FMerinOak02__98b2088d.png?alt=media&token=c35ae39a-b973-43fb-aa28-11c480e2d932',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FiH2ZUlBC2JYCy5uWxES7CI3Z1y32%2FMerinOak03__e290785c.png?alt=media&token=709d968d-5e76-45c6-a73e-8d283526bddd',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FiH2ZUlBC2JYCy5uWxES7CI3Z1y32%2FMerinOak04__341f9ee0.png?alt=media&token=904ba1a2-9d39-4886-a9bc-af500c44d119'
    ]
  },
  {
    id: 'spc-7',
    name: 'Turin Oak',
    slug: 'turin-oak-nl-002',
    series: 'SPC LAMINATE',
    code: 'NL-002',
    palette: 'NATURE LIGHT',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Светлая природная тональность — для воздуха, света и простора.',
    fullDescription: 'Turin Oak создан для интерьеров, где хочется больше света и спокойствия. Натуральный светлый оттенок красиво раскрывается при дневном освещении и визуально «расширяет» комнату. Уместен в спальнях, детских и уютных гостиных.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FTurinOak01__c7365bf1.png?alt=media&token=01a14bc0-9da1-4846-899a-2cf948d3ebc2',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FTurinOak02__011600a0.png?alt=media&token=bf52fa69-436b-4213-b322-1b4fc784da12',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FTurinOak03__396d860b.png?alt=media&token=4ae12a0c-f542-4f37-8c09-ea231853d5e4'
    ]
  },
  {
    id: 'spc-8',
    name: 'Gecse Oak',
    slug: 'gecse-oak-nm-002',
    series: 'SPC LAMINATE',
    code: 'NM-002',
    palette: 'NATURE MEDIUM',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Тёплый средний дуб — гармония и уверенный уют.',
    fullDescription: 'Gecse Oak даёт интерьеру собранность и тёплую глубину, оставаясь универсальным. Этот оттенок хорошо поддерживает мебель из дерева и тёплые нейтрали. Для спокойного дизайна без резких контрастов.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FzF8RrlDr9MRnT8m9hqqVdJg2Pji2%2FGecseOak01__788845c3.png?alt=media&token=e4516c59-2615-4335-bc69-84a58c34db51',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FzF8RrlDr9MRnT8m9hqqVdJg2Pji2%2F02__03f337e6.png?alt=media&token=2e1faeb2-2ea4-4264-a4ff-05c7f8604404',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FzF8RrlDr9MRnT8m9hqqVdJg2Pji2%2F03__a913c91e.png?alt=media&token=9e28a643-6c85-4efc-b1b3-0578798ebc1e'
    ]
  },
  {
    id: 'spc-9',
    name: 'Bargo Oak',
    slug: 'bargo-oak-nd-002',
    series: 'SPC LAMINATE',
    code: 'ND-002',
    palette: 'NATURE DARK',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Тёмный дуб для контраста — графика и глубина пространства.',
    fullDescription: 'Bargo Oak помогает сделать интерьер выразительным: на его фоне красиво смотрятся светлые стены и крупные формы мебели. Тёмная палитра добавляет глубины и визуальной «плотности». Хорош для лофта и современных офисов.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FvPHrVFs1L1amA5l2PQVD9I1kXew1%2FBargoOak01__390ce898.png?alt=media&token=cfe62862-4532-4afe-a5e4-72fb1a37d5eb',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FvPHrVFs1L1amA5l2PQVD9I1kXew1%2FBargoOak02__5e85ef66.png?alt=media&token=75ff02d3-26a0-424e-95fd-1f6fedaf52fe',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FvPHrVFs1L1amA5l2PQVD9I1kXew1%2FBargoOak03__32023b53.png?alt=media&token=f7033f26-0034-4153-ba53-20f516c28ca1',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FvPHrVFs1L1amA5l2PQVD9I1kXew1%2FBargoOak04__6d2952e6.png?alt=media&token=428de907-a8dc-4abc-a500-62fb6a3d6e21'
    ]
  },
  {
    id: 'spc-10',
    name: 'Suva Oak',
    slug: 'suva-oak-gr-003',
    series: 'SPC LAMINATE',
    code: 'GR-003',
    palette: 'GREY',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Сдержанный серый дуб — аккуратный и практичный.',
    fullDescription: 'Suva Oak — вариант для тех, кто ценит нейтральность и чистый вид. Серый тон легко комбинируется с любыми материалами и цветами. Подойдёт для коридоров, open-space и коммерческих зон.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FSuvaOak01__3c2d8053.png?alt=media&token=3ebd673e-54b7-4a46-bf88-8bf5a6e12f40',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FSuvaOak02__88866e6a.png?alt=media&token=8a80335b-f31d-46d4-bded-bb7da4a13f82',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FSuvaOak03__4234bb4a.png?alt=media&token=7490bb17-7116-444c-a241-83218656a65e',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FSuvaOak04__77e26304.png?alt=media&token=b1dbb0a1-86de-4ad5-8d80-c96b61c18328'
    ]
  },
  {
    id: 'spc-11',
    name: 'Lena Oak',
    slug: 'lena-oak-nl-003',
    series: 'SPC LAMINATE',
    code: 'NL-003',
    palette: 'NATURE LIGHT',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Натуральный светлый дуб — лёгкость и домашнее тепло.',
    fullDescription: 'Lena Oak создаёт мягкое «домашнее» настроение и хорошо работает в светлых интерьерах. Поддерживает скандинавский стиль и уютный минимализм. Сочетается с белой мебелью и текстилем натуральных тонов.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FiH2ZUlBC2JYCy5uWxES7CI3Z1y32%2FLenaOak01__56dfa81a.png?alt=media&token=e5b902f0-4fa1-4d98-9630-f98dd16e24d7',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FiH2ZUlBC2JYCy5uWxES7CI3Z1y32%2FLenaOak02__a313cb8b.png?alt=media&token=969221be-f66b-43a3-a90c-b3e18969caca',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FiH2ZUlBC2JYCy5uWxES7CI3Z1y32%2FLenaOak03__13ea41bb.png?alt=media&token=d99fd576-8422-470a-9be8-f41f4d8dfadb'
    ]
  },
  {
    id: 'spc-12',
    name: 'Vento Oak',
    slug: 'vento-oak-nm-003',
    series: 'SPC LAMINATE',
    code: 'NM-003',
    palette: 'NATURE MEDIUM',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Средний натуральный дуб — ровный тон для продуманного дизайна.',
    fullDescription: 'Vento Oak — баланс между светлыми и тёмными декорами. Он подчёркивает фактуру мебели и не перетягивает внимание на себя. Подходит для проекта с спокойной, собранной подачей.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FVentoOak01__8a942072.png?alt=media&token=4722a5cc-980d-4922-aa2d-7be76871e404',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FVentoOak02__4be1550c.png?alt=media&token=0aa54efe-e33f-4852-b8eb-2bf7c461a093',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FVentoOak03__6c1ccfc9.png?alt=media&token=a6abccb8-e82e-4502-8856-f0843bb207c6'
    ]
  },
  {
    id: 'spc-13',
    name: 'Tiffin Oak',
    slug: 'tiffin-oak-nd-003',
    series: 'SPC LAMINATE',
    code: 'ND-003',
    palette: 'NATURE DARK',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Тёмный дуб с глубиной — камерная атмосфера и характер.',
    fullDescription: 'Tiffin Oak делает интерьер более насыщенным и «взрослым». Хорошо сочетается с тёплым светом, латунью и чёрной фурнитурой. Подойдёт для кабинетов, салонов и премиальных зон.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FTiffinOak01__0add2cb2.png?alt=media&token=64b09c99-24d7-4f58-bd39-afe9edc43aa5',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FTiffinOak02__8175e2dc.png?alt=media&token=6422462e-f4e0-46a7-91c6-8f75ad7f6561',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FTiffinOak03__6c69767b.png?alt=media&token=bcdcd86b-d6bc-4f43-bd7c-6bb23f5b5b9b'
    ]
  },
  {
    id: 'spc-14',
    name: 'Taku Oak',
    slug: 'taku-oak-gr-004',
    series: 'SPC LAMINATE',
    code: 'GR-004',
    palette: 'GREY',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Графичный серый дуб — современно и строго.',
    fullDescription: 'Taku Oak подойдёт для интерьеров с чёткой геометрией. Серый тон создаёт ощущение порядка и легко дружит с камнем и металлом. Практичный вариант для рабочих и общественных пространств.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FTakuOak01__30a7c8fc.png?alt=media&token=5a100679-d740-421d-b440-f34f34e7dea6',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FTakuOak02__5110a0a4.png?alt=media&token=b04a7661-089c-4a27-9bf8-17419c34e39e',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FTakuOak03__9f276f9e.png?alt=media&token=559956e9-4479-4383-97f5-fda9573f4c3f',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FTakuOak04__7ea9794b.png?alt=media&token=ea9542a8-43a1-43c1-b1d2-6c1e125b43f1'
    ]
  },
  {
    id: 'spc-15',
    name: 'Luten Oak',
    slug: 'luten-oak-nl-004',
    series: 'SPC LAMINATE',
    code: 'NL-004',
    palette: 'NATURE LIGHT',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Светлый дуб — мягкий фон для мебели и декора.',
    fullDescription: 'Luten Oak помогает создать светлый интерьер без «стерильности». Хорошо сочетается с бежевыми и молочными палитрами. Подходит для квартир, гостиничных номеров и шоурумов.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FiH2ZUlBC2JYCy5uWxES7CI3Z1y32%2FLutenOak01__91965ff1.png?alt=media&token=3ab57c5c-aec8-433d-aa5c-682c2f1e2750',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FiH2ZUlBC2JYCy5uWxES7CI3Z1y32%2FLutenOak02__65fcb0e1.png?alt=media&token=a2332381-f46a-4f06-8040-84f94f094134',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FiH2ZUlBC2JYCy5uWxES7CI3Z1y32%2FLutenOak03__9f702c29.png?alt=media&token=6378e9be-cf15-4316-b72c-ec57b27f8041'
    ]
  },
  {
    id: 'spc-16',
    name: 'Maca Oak',
    slug: 'maca-oak-nm-004',
    series: 'SPC LAMINATE',
    code: 'NM-004',
    palette: 'NATURE MEDIUM',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Тёплый средний дуб — комфортный тон на каждый день.',
    fullDescription: 'Maca Oak создаёт спокойную атмосферу и хорошо выглядит при разном освещении. Универсален для большинства цветовых схем. Уместен в кухне-гостиной и семейных пространствах.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FiH2ZUlBC2JYCy5uWxES7CI3Z1y32%2FMacaOak01__cbb0dace.png?alt=media&token=73c34013-41bb-4f72-af7e-4d3abf9c8b4e',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FiH2ZUlBC2JYCy5uWxES7CI3Z1y32%2FMacaOak02__d286f1ab.png?alt=media&token=022b4bca-d829-424c-b2d2-6cdd82efb697',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FiH2ZUlBC2JYCy5uWxES7CI3Z1y32%2FMacaOak03__780aa5ff.png?alt=media&token=cd078e2e-5461-4822-8ccd-886ffd7cd98e'
    ]
  },
  {
    id: 'spc-17',
    name: 'Ranea Walnut',
    slug: 'ranea-walnut-nd-004',
    series: 'SPC LAMINATE',
    code: 'ND-004',
    palette: 'NATURE DARK',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Тёмный орех — благородный характер и выразительная глубина.',
    fullDescription: 'Ranea Walnut добавляет ощущение «дорогого» дерева и делает интерьер теплее. Хорошо сочетается с кремовыми стенами и мягким светом. Подходит для классики и современных премиальных решений.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FRaneaWalnut01__28654a94.png?alt=media&token=36fd28ba-ebca-4ed6-bfcb-b1031acc9445',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2F02__b75c0619.png?alt=media&token=f7fb04b9-5deb-4293-b466-d9f22a407b88',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2F03__b0b544e5.png?alt=media&token=d75b335c-83f7-4d3f-b1fe-9c801f0ef894'
    ]
  },
  {
    id: 'spc-18',
    name: 'Dunster Oak',
    slug: 'dunster-oak-nl-005',
    series: 'SPC LAMINATE',
    code: 'NL-005',
    palette: 'NATURE LIGHT',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Светлый дуб — максимум света, минимум визуального шума.',
    fullDescription: 'Dunster Oak — спокойный фон для интерьера, где важны простор и свет. Натуральный светлый оттенок визуально облегчает мебель и пространство. Хорош для небольших комнат и студий.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FzF8RrlDr9MRnT8m9hqqVdJg2Pji2%2FDunsterOak01__b00622fd.png?alt=media&token=cd550285-6d29-4747-89df-283f66619bd2',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FzF8RrlDr9MRnT8m9hqqVdJg2Pji2%2FDunsterOak02__23454420.png?alt=media&token=4e3cee1d-b251-4615-b69b-e14ea61bd007',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FzF8RrlDr9MRnT8m9hqqVdJg2Pji2%2FDunsterOak03__e3588767.png?alt=media&token=e738d0a8-9cc6-4051-90ed-dde798d186cc'
    ]
  },
  {
    id: 'spc-19',
    name: 'Chewelah Kickory',
    slug: 'chewelah-kickory-nm-005',
    series: 'SPC LAMINATE',
    code: 'NM-005',
    palette: 'NATURE MEDIUM',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Хикори средней тональности — живой рисунок и тёплая динамика.',
    fullDescription: 'Chewelah Hickory добавляет характер и ощущение натуральной древесной энергии. Средний оттенок остаётся универсальным, но выглядит выразительнее классического дуба. Подходит, когда нужен «акцент без перегруза».',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FvPHrVFs1L1amA5l2PQVD9I1kXew1%2FChewelahKickory01__c3bdb739.png?alt=media&token=732cbef6-deb1-40e1-84e7-7477157b8ad2',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FvPHrVFs1L1amA5l2PQVD9I1kXew1%2FChewelahKickory02__c0d3d3ed.png?alt=media&token=1235f727-65bb-46dd-aa3d-8f8b3ebac313',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FvPHrVFs1L1amA5l2PQVD9I1kXew1%2FChewelahKickory03__426811df.png?alt=media&token=66797a71-4356-4282-b908-5664d984ac6a'
    ]
  },
  {
    id: 'spc-20',
    name: 'Juba Oak',
    slug: 'juba-oak-nd-005',
    series: 'SPC LAMINATE',
    code: 'ND-005',
    palette: 'NATURE DARK',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Тёмный дуб — контрастный, выразительный, уверенный.',
    fullDescription: 'Juba Oak подчёркивает светлые стены и делает интерьер более структурным. Тёмная палитра красиво работает с дизайнерским светом и минималистичной мебелью. Уместен в холлах и офисных пространствах.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2F7fA6t0FVT2dql5bWqYILtlCZjB82%2FJubaOak01__e1689c99.png?alt=media&token=abdb0e98-d51d-43a6-99e0-f75480e0a2be',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2F7fA6t0FVT2dql5bWqYILtlCZjB82%2FJubaOak02__ebafc20c.png?alt=media&token=3837fc56-eff4-4fca-af53-8b0b6a4c147f',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2F7fA6t0FVT2dql5bWqYILtlCZjB82%2FJubaOak03__d6cfac63.png?alt=media&token=020c5c91-dd0d-46f0-9fc3-481785dac85b'
    ]
  },
  {
    id: 'spc-21',
    name: 'Ruma Oak',
    slug: 'ruma-oak-nl-006',
    series: 'SPC LAMINATE',
    code: 'NL-006',
    palette: 'NATURE LIGHT',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Светлый натуральный дуб — мягкое тепло и лёгкая подача.',
    fullDescription: 'Ruma Oak делает интерьер светлее и спокойнее. Натуральная светлая палитра хорошо сочетается с текстилем и светлой мебелью. Отлично для уютных, «воздушных» дизайнов.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FRumaOak01__041f2621.png?alt=media&token=15dae050-3e03-4fd7-aa01-87ac172b9724',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FRumaOak02__87bd3977.png?alt=media&token=842d21e3-5a73-419b-86e5-832a8f510625',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FRumaOak03__83653af3.png?alt=media&token=420fdca4-44b0-4254-9a8f-90d2d19a18b2'
    ]
  },
  {
    id: 'spc-22',
    name: 'Nadi Oak',
    slug: 'nadi-oak-nm-006',
    series: 'SPC LAMINATE',
    code: 'NM-006',
    palette: 'NATURE MEDIUM',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Средний дуб — универсальная тональность для продуманных проектов.',
    fullDescription: 'Nadi Oak выглядит сбалансированно и аккуратно. Поддерживает как тёплые, так и нейтральные схемы интерьера. Хороший выбор, когда нужен надёжный «средний» тон.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FiH2ZUlBC2JYCy5uWxES7CI3Z1y32%2FNadiOak01__3ae68db3.png?alt=media&token=e9abf3df-6639-437d-81cd-0cbf910762e3',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FiH2ZUlBC2JYCy5uWxES7CI3Z1y32%2F02__307916bc.png?alt=media&token=fa7987f6-1ba9-448e-8b6c-ad14588f0c8e',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FiH2ZUlBC2JYCy5uWxES7CI3Z1y32%2F03__8c438308.png?alt=media&token=c545de7f-8488-4d76-83b4-c114b123afe0',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FiH2ZUlBC2JYCy5uWxES7CI3Z1y32%2F04__520a4560.png?alt=media&token=8a43dafc-3997-4dc6-92c1-56b9143766f4'
    ]
  },
  {
    id: 'spc-23',
    name: 'Zubri Oak',
    slug: 'zubri-oak-nl-007',
    series: 'SPC LAMINATE',
    code: 'NL-007',
    palette: 'NATURE LIGHT',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Светлая природная палитра — свежесть и лёгкость в интерьере.',
    fullDescription: 'Zubri Oak делает пространство визуально чище и светлее. Хорошо вписывается в современный минимализм и сканди-настроение. Сочетается с белым, серым и светлым деревом.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2Fimage__cf2848f7.png?alt=media&token=69124361-d810-42d8-9361-fb026c58aa48',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FZubriOak02__02c1ab24.png?alt=media&token=9fb27be6-d355-47e3-8ee5-77afb51bb5a1',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FZubriOak03__2099657e.png?alt=media&token=ab33352f-8a67-46af-a054-1f6585317ef3'
    ]
  },
  {
    id: 'spc-24',
    name: 'Hatton Oak',
    slug: 'hatton-oak-nm-007',
    series: 'SPC LAMINATE',
    code: 'NM-007',
    palette: 'NATURE MEDIUM',
    layingTypes: ['Палуба', 'Ёлочка'],
    shortDescription: 'Тёплый средний дуб — комфортный баланс и спокойная фактура.',
    fullDescription: 'Hatton Oak — гармоничный вариант для интерьеров «на каждый день». Средняя натуральная палитра сохраняет уют и не перегружает пространство. Подходит для жилых комнат, коридоров и коммерческих зон.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Стандарт',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FHattonOak01__e4e48759.png?alt=media&token=64478d84-d3e8-46b3-80f8-0588719a0429',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FHattonOak02__56fbb1fa.png?alt=media&token=572f55fe-25d8-4c20-b27c-a01bf77f9a2c',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Fce4zENTKNCPqsNq10i1Qv0KFger1%2FHattonOak03__07c8e080.png?alt=media&token=78392a15-e6fd-4992-94cb-44ffc4025f55'
    ]
  },
  {
    id: 'spc-25',
    name: 'Dives Concrete',
    slug: 'dives-concrete-stl-001',
    series: 'SPC LAMINATE',
    code: 'STL-001',
    palette: 'STONE',
    installationType: 'Плитка',
    layingTypes: ['Плитка'],
    sizesTile: ['600 × 600 mm', '600 × 300 mm', '1200 × 600 mm'],
    shortDescription: 'Бетонный декор — чистый лофт и строгая геометрия.',
    fullDescription: 'Dives Concrete создаёт современный «индустриальный» характер без лишней тяжести. Бетонная фактура хорошо сочетается с металлом, стеклом и монохромной мебелью. Отлично подходит для офисов, шоурумов и минималистичных интерьеров.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Камень',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FzF8RrlDr9MRnT8m9hqqVdJg2Pji2%2FDivesConcrete01__ba6aba07.png?alt=media&token=51fcda64-b56c-40eb-baca-e43de8735eef',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FzF8RrlDr9MRnT8m9hqqVdJg2Pji2%2F02__abdd00d9.png?alt=media&token=5ae3b741-6712-4346-98f6-3cedecaced17',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FzF8RrlDr9MRnT8m9hqqVdJg2Pji2%2F03__9e2c48cb.png?alt=media&token=8eb89720-d340-4ac4-8d8d-aee920029284'
    ]
  },
  {
    id: 'spc-26',
    name: 'Cuts Terrazzo',
    slug: 'cuts-terrazzo-stl-002',
    series: 'SPC LAMINATE',
    code: 'STL-002',
    palette: 'STONE',
    installationType: 'Плитка',
    layingTypes: ['Плитка'],
    sizesTile: ['600 × 600 mm', '600 × 300 mm', '1200 × 600 mm'],
    shortDescription: 'Терраццо — стильный рисунок с архитектурным настроением.',
    fullDescription: 'Cuts Terrazzo добавляет интерьеру динамику за счёт характерного крапчатого эффекта. Такой декор хорошо смотрится в современных проектах и легко поддерживает цветовые акценты. Подходит для кухонь-гостиных, кафе и коммерческих пространств.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Камень',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FzF8RrlDr9MRnT8m9hqqVdJg2Pji2%2FCutsTerrazzo01__1a83a729.png?alt=media&token=21abebc0-65df-40d4-adcd-811429c8e1f1',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FzF8RrlDr9MRnT8m9hqqVdJg2Pji2%2FCutsTerrazzo02__bd9103b5.png?alt=media&token=c1376838-2886-497c-9c35-c93e38aaf04c',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FzF8RrlDr9MRnT8m9hqqVdJg2Pji2%2FCutsTerrazzo03__315e2787.png?alt=media&token=9c16196b-84e6-4467-be9d-7093fb38b41d'
    ]
  },
  {
    id: 'spc-27',
    name: 'Camelin Marble',
    slug: 'camelin-marble-stl-003',
    series: 'SPC LAMINATE',
    code: 'STL-003',
    palette: 'STONE',
    installationType: 'Плитка',
    layingTypes: ['Плитка'],
    sizesTile: ['600 × 600 mm', '600 × 300 mm', '1200 × 600 mm'],
    shortDescription: 'Мраморный декор — лёгкая классика в современном прочтении.',
    fullDescription: 'Camelin Marble делает пространство визуально чище и «светлее» за счёт благородной каменной подачи. Хорошо сочетается with тёплым светом, латунью и спокойными нейтралями. Уместен в интерьерах, где нужен эффект аккуратной премиальности.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Камень',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FzF8RrlDr9MRnT8m9hqqVdJg2Pji2%2FCamelinMarble01__e3b8dbed.png?alt=media&token=4dafb891-dce1-4ca4-b060-6b976cda7289',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FzF8RrlDr9MRnT8m9hqqVdJg2Pji2%2FCamelinMarble02__edb9d518.png?alt=media&token=0d0bac83-cea7-4899-aa91-f860a839b725',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FzF8RrlDr9MRnT8m9hqqVdJg2Pji2%2FCamelinMarble03__1738c7f2.png?alt=media&token=b9e97e97-e04d-4f84-aed8-b3a050f7b851'
    ]
  },
  {
    id: 'spc-28',
    name: 'Kake Waterstone',
    slug: 'kake-waterstone-stl-004',
    series: 'SPC LAMINATE',
    code: 'STL-004',
    palette: 'STONE',
    installationType: 'Плитка',
    layingTypes: ['Плитка'],
    sizesTile: ['600 × 600 mm', '600 × 300 mm', '1200 × 600 mm'],
    shortDescription: 'Каменная текстура с мягкой глубиной — спокойный акцент без перегруза.',
    fullDescription: 'Kake Waterstone выглядит сдержанно, но интересно: каменный рисунок добавляет фактуру и ощущение «материальности». Такой декор легко комбинировать с деревом и однотонными стенами. Отличный вариант для холлов, салонов и современных квартир.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Камень',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2F7fA6t0FVT2dql5bWqYILtlCZjB82%2FKakeWaterstone01__94d1ca7d.png?alt=media&token=df5230f2-d553-44f0-b1fb-63a8a46ff32f',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2F7fA6t0FVT2dql5bWqYILtlCZjB82%2FKakeWaterstone02__018d1d7a.png?alt=media&token=9dbd3159-cbef-4203-8873-a60d201757f9',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2F7fA6t0FVT2dql5bWqYILtlCZjB82%2FKakeWaterstone04__f2a1a6ce.png?alt=media&token=00a08cab-b284-4a2c-86b3-c829761f481b'
    ]
  },
  {
    id: 'spc-29',
    name: 'Branzi Stone',
    slug: 'branzi-stone-stl-005',
    series: 'SPC LAMINATE',
    code: 'STL-005',
    palette: 'STONE',
    installationType: 'Плитка',
    layingTypes: ['Плитка'],
    sizesTile: ['600 × 600 mm', '600 × 300 mm', '1200 × 600 mm'],
    shortDescription: 'Каменный декор — строгий, собранный, уверенный.',
    fullDescription: 'Branzi Stone подчёркивает архитектуру пространства и хорошо работает в лаконичных интерьерах. Каменная подача гармонирует с чёрными деталями, стеклом и тёплой подсветкой. Подходит для зон, где важны аккуратность и современная эстетика.',
    specs: {
      thickness: '4–8 mm',
      lockType: 'Uniclic',
      embossing: 'Камень',
      underlay: 'IXPE'
    },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Ft5DgsuksCtOQpJNpoXHLHBp7fWg2%2FBranziStone041__30969ddd.png?alt=media&token=cf02143c-a6b6-4311-a34a-6008a62dd589',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Ft5DgsuksCtOQpJNpoXHLHBp7fWg2%2F01__feab16a1.png?alt=media&token=6a1b2dbd-a45b-4afe-b745-1a070732f7fa',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Ft5DgsuksCtOQpJNpoXHLHBp7fWg2%2F02__70267042.png?alt=media&token=8c4d08ed-c1ef-494d-a710-9a64b00ee8d7',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2Ft5DgsuksCtOQpJNpoXHLHBp7fWg2%2F03__fcdf7273.png?alt=media&token=252f033d-ef5c-4c16-8466-b2c76826b816'
    ]
  },
  // OTHER SERIES
  // SPC PANELS - 19 Products
  {
    id: 'spc-panel-1',
    name: 'Smoked Ash',
    slug: 'smoked-ash-wd-001',
    series: 'SPC PANELS',
    code: 'WD-001',
    groupType: 'WD',
    colorTag: 'grey',
    shortDescription: {
      ru: 'Дымчатый ясень с холодной глубиной — строгий фон для акцентной стены.',
      en: 'Smoky ash with a cool depth — a strict, premium backdrop for an accent wall.',
      uz: 'Tutunli kulrang yasen — aksent devor uchun sovuq, premium fon.'
    },
    fullDescription: {
      ru: 'Текстура ясеня с выраженным волокном и мягким дымчато‑серым тоном.\nЛучше всего раскрывается в гостиной, холле, коридоре и зонах ожидания.\nСочетается с чёрным металлом, светлым камнем, графитовой мебелью и тёплой подсветкой.\nПрактична для проходных мест: поверхность легко очищается, визуально скрывает мелкие следы.\nИдеальна для современных интерьеров, лофта и минимализма, когда нужен «спокойный характер» без лишней декоративности.',
      en: 'Ash grain with a pronounced fibre and a soft smoky-grey tone.\nLooks best on feature walls in living rooms, halls, corridors and reception areas.\nPairs well with black metal, light stone, graphite furniture and warm accent lighting.\nPractical for high-traffic spaces: easy to wipe clean and forgiving to everyday marks.\nA strong fit for modern, loft and minimal interiors when you want calm sophistication without visual noise.',
      uz: 'Yasen tolalari aniq ko‘rinadigan, tutunli kulrang ohangdagi tekstura.\nMehmonxona, xoll, yo‘lak va resepshen zonalarida aksent devor sifatida juda chiroyli ochiladi.\nQora metall, och tosh, grafit mebel va iliq yoritish bilan uyg‘unlashadi.\nKo‘p yuriladigan joylar uchun amaliy: yuzasi oson tozalanadi, mayda izlar kam bilinadi.\nZamonaviy, loft va minimalizm uslublarida ortiqcha bezaksiz, lekin premium kayfiyat yaratadi.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: '/media/SmokedAsh01.png',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FSmokedAsh02__20b43cad.png?alt=media&token=705b1783-d00b-49c5-ab30-7b406d9d40e8',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FSmokedAsh03__61e2f8d6.png?alt=media&token=f104a638-85f7-45b0-af8f-2b40fa99413b'
    ]
  },
  {
    id: 'spc-panel-2',
    name: 'Charcoal Oak',
    slug: 'charcoal-oak-wd-002',
    series: 'SPC PANELS',
    code: 'WD-002',
    groupType: 'WD',
    colorTag: 'dark',
    shortDescription: {
      ru: 'Угольный дуб — графика и статус, когда интерьер держит линию.',
      en: 'Charcoal oak — graphic, confident and undeniably premium.',
      uz: 'Ko‘mir rang dub — qat’iy grafika va premium kayfiyat.'
    },
    fullDescription: {
      ru: 'Темная дубовая фактура с ровным угольным подтоном и «сухой» графикой.\nОтлично работает в коммерции: ресепшн, переговорные, шоурумы, коридоры отелей.\nВ жилых интерьерах идеальна для ТВ‑зоны или стены за изголовьем.\nСочетается с бетоном, тёмным камнем, латунью, стеклом и тёплым направленным светом.\nПрактичный акцент: визуально дисциплинирует пространство и сохраняет аккуратный вид при ежедневном уходе.\nРекомендуемые стили: современный, лофт, индустриальный минимализм.',
      en: 'Dark oak texture with an even charcoal undertone and a clean, architectural grain.\nA natural choice for commercial interiors: reception desks, meeting rooms, showrooms and hotel corridors.\nIn residential spaces it shines as a TV wall or behind the headboard.\nCombines beautifully with concrete, dark stone, brass, glass and warm directional lighting.\nA practical statement surface that stays composed with simple everyday cleaning.\nBest for modern, loft and industrial-minimal styles.',
      uz: 'Ko‘mir ohangdagi dub teksturasi — chiziqlari aniq, ko‘rinishi tartibli.\nResepshen, muzokara xonasi, shourum va mehmonxona yo‘laklarida juda ishonchli ko‘rinadi.\nUyda TV‑zona yoki yotoqda bosh qismi ortidagi devor uchun ideal.\nBeton, to‘q tosh, latun, shisha va iliq yo‘naltirilgan yoritish bilan moslashadi.\nOddiy parvarish bilan ham premium ko‘rinishini saqlaydi, aksent devor sifatida kuchli ishlaydi.\nUslub tavsiyasi: zamonaviy, loft, industrial minimalizm.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FCharcoalOak01__3ea8e88f.png?alt=media&token=e87064e2-58d5-43d8-8ba3-bc854e6458bf',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FCharcoalOak02__7be8107c.png?alt=media&token=5ea780ea-fc0e-45a1-adbf-eabd05189892',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FCharcoalOak03__758f3fd0.png?alt=media&token=b6a29bbd-ea4d-46e9-be72-ef8dae497b20'
    ]
  },
  {
    id: 'spc-panel-3',
    name: 'Natural Oak',
    slug: 'natural-oak-wd-003',
    series: 'SPC PANELS',
    code: 'WD-003',
    groupType: 'WD',
    colorTag: 'light',
    shortDescription: {
      ru: 'Натуральный дуб — светлый баланс и «дорогая» естественность.',
      en: 'Natural oak — bright balance and refined, authentic warmth.',
      uz: 'Natural dub — yorug‘ muvozanat va tabiiy premium iliqlik.'
    },
    fullDescription: {
      ru: 'Светлый дуб с мягкой, натуральной текстурой без желтизны и лишнего контраста.\nХорош для больших плоскостей: гостиные, кухни‑столовые, офисы, коридоры.\nСобирает интерьер в «чистый» образ и визуально расширяет пространство.\nСочетается с тёплым светом, белыми/молочными стенами, чёрной фурнитурой и натуральным текстилем.\nПрактичен для повседневного использования: прост в уходе, подходит для акцентных и фоновых стен.\nРекомендованные стили: современный минимализм, сканди, мягкая неоклассика.',
      en: 'Light oak with a natural grain — clean, calm and free from heavy yellow tones.\nPerfect for large wall planes in living rooms, kitchen-dining areas, offices and corridors.\nVisually opens up the space and keeps the interior feeling bright and precise.\nPairs with warm lighting, off-white walls, black hardware and natural textiles.\nEasy to maintain and equally good as a feature or background wall finish.\nRecommended styles: modern minimal, Scandinavian, soft neo-classic.',
      uz: 'Sarg‘aymagan, ortiqcha kontrastsiz och dub teksturasi — tabiiy va tartibli.\nMehmonxona, oshxona‑ovqatlanish zonasi, ofis va yo‘laklarda katta devorlar uchun juda mos.\nXonani vizual ravishda kengaytiradi va yorug‘ kayfiyat beradi.\nIliq yoritish, sut rang devorlar, qora furnitura va tabiiy matolar bilan uyg‘un.\nKundalik parvarishi oson; aksent devor ham, fon devor ham sifatli ko‘rinadi.\nUslub tavsiyasi: zamonaviy minimalizm, skandi, yumshoq neoklassika.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: '/media/NaturalOak01.png',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FNaturalOak03__16bbc3b6.png?alt=media&token=486e79cb-2494-4ba9-9c70-1610f1d97a77',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FNaturalOak02__0590d56a.png?alt=media&token=d3609b1e-e84f-4a0d-9b57-9fe36b8ecf1d'
    ]
  },
  {
    id: 'spc-panel-4',
    name: 'Cognac Walnut',
    slug: 'cognac-walnut-wd-004',
    series: 'SPC PANELS',
    code: 'WD-004',
    groupType: 'WD',
    colorTag: 'medium',
    shortDescription: {
      ru: 'Коньячный орех — тёплый акцент для уютной премиальной атмосферы.',
      en: 'Cognac walnut — warm, inviting and unmistakably premium.',
      uz: 'Konyak ohangdagi yong‘oq — iliq, premium aksent.'
    },
    fullDescription: {
      ru: 'Ореховая текстура средней тональности с мягким коньячным оттенком и благородной глубиной.\nИдеальна для гостиной, кабинета, домашней библиотеки, boutique‑пространств.\nСочетается с камнем тёплых оттенков, латунью, бронзой и мягким тёплым светом.\nДаёт «дорогое» ощущение дерева, но без излишней темноты — комфортно даже в небольших комнатах.\nПрактичный выбор для акцентных стен: легко поддерживать чистоту, поверхность выглядит ровно и аккуратно.\nРекомендованные стили: современная классика, неоклассика, Japandi, contemporary.',
      en: 'Walnut texture in a medium tone with a refined cognac warmth and a calm depth.\nGreat for living rooms, home offices, libraries and boutique retail interiors.\nGives a “real wood” premium feeling without making the room too dark.\nIdeal for feature walls: easy daily care and a consistently neat look.\nRecommended styles: modern classic, neo-classic, Japandi, contemporary.',
      uz: 'O‘rtacha tonli yong‘oq teksturasi — konyak iliqligi va nafis chuqurlik.\nMehmonxona, kabinet, uy kutubxonasi hamda boutique maydonlar uchun mos.\nXonani qorong‘ilatmay, yog‘ochning premium hissini beradi — kichik xonalarda ham qulay.\nAksent devor sifatida amaliy: parvarishi oson, ko‘rinishi doim tartibli.\nUslub tavsiyasi: modern klassika, neoklassika, Japandi, contemporary.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FCognacWalnut01__4cc5e536.png?alt=media&token=78f2feac-a326-4836-b17b-96f1ced57947',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FCognacWalnut03__ee05a544.png?alt=media&token=d6243db3-cc74-4e5c-8085-9e090d1216a9',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FCognacWalnut02__2d6af6ab.png?alt=media&token=a85e0bc9-6695-40bc-8870-a2648f65caa6'
    ]
  },
  {
    id: 'spc-panel-5',
    name: 'Espresso Walnut',
    slug: 'espresso-walnut-wd-005',
    series: 'SPC PANELS',
    code: 'WD-005',
    groupType: 'WD',
    colorTag: 'dark',
    shortDescription: {
      ru: 'Эспрессо‑орех — глубокий тон для статусных стен и света по фактуре.',
      en: 'Espresso walnut — deep, dramatic and premium for statement walls.',
      uz: 'Espresso yong‘oq — chuqur ton, statusli aksent devor uchun.'
    },
    fullDescription: {
      ru: 'Плотный тёмный орех с насыщенной глубиной и выразительной древесной графикой.\nЛучшее применение — ресепшн, переговорные, кабинеты, зоны премиального сервиса.\nВ доме хорошо смотрится за изголовьем, в ТВ‑зоне или в нишах с подсветкой.\nСочетается с тёплым светом, кожей, латунью, чёрными деталями и камнем.\nПрактичный акцент: на тёмной поверхности мелкие следы менее заметны, уход простой.\nРекомендодованные стили: contemporary, лофт, современная классика с контрастом.',
      en: 'A dense dark walnut with rich depth and a strong, elegant wood pattern.\nBest used in receptions, meeting rooms, executive offices and premium service zones.\nAt home it looks great behind the bed, on a TV wall or inside backlit niches.\nPairs with warm light, leather, brass, black details and stone surfaces.\nA practical statement: daily marks are less visible, and cleaning stays simple.\nRecommended styles: contemporary, loft, contrast-driven modern classic.',
      uz: 'To‘q yong‘oq teksturasi — chuqur rang va aniq yog‘och grafikasiga ega.\nResepshen, muzokara xonalari, rahbar kabinetlari va premium xizmat zonalarida juda kuchli ishlaydi.\nUyda yotoq bosh qismi, TV‑zona yoki yoritilgan nishalarda chiroyli ko‘rinadi.\nIliq yoritish, charm, latun, qora detallar va tosh bilan moslashadi.\nAksent devorlari uchun amaliy: parvarishi oson, ko‘rinishi doim tartibli.\nUslub tavsiyasi: contemporary, loft, kontrastli modern klassika.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: '/media/EspressoWalnut01_new.png',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FEspressoWalnut02__703f5c65.png?alt=media&token=50b78cb9-332e-4cfa-a452-0b94a705b357',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FEspressoWalnut03__bf9dda0d.png?alt=media&token=e96aba23-fd15-4b1a-8a15-b154049056f4'
    ]
  },
  {
    id: 'spc-panel-6',
    name: 'Ivory Maple',
    slug: 'ivory-maple-wd-006',
    series: 'SPC PANELS',
    code: 'WD-006',
    groupType: 'WD',
    colorTag: 'light',
    shortDescription: {
      ru: 'Слоновая кость и клён — светлая панель для чистого, тёплого минимализма.',
      en: 'Ivory maple — light, calm and premium for bright minimalist spaces.',
      uz: 'Ivory maple — yorug‘, sokin va minimalizm uchun premium yechim.'
    },
    fullDescription: {
      ru: 'Светлая кленовая текстура с кремовым подтоном — мягкий свет без «стерильности».\nОтлично подходит для квартир, коридоров, гостиничных номеров и офисных зон.\nХорошо сочетается с белыми стенами, светлым камнем, тёплыми тканями и деревом.\nДелает пространство визуально выше и чище, особенно при дневном освещении.\nПрактична для фоновых стен: проста в уходе, хорошо смотрится на больших плоскостях.\nРекомендодованные стили: сканди, Japandi, современный минимализм, лёгкая неоклассика.',
      en: 'A light maple texture with a creamy undertone — bright, warm and never sterile.\nPerfect for apartments, corridors, hotel rooms and office areas.\nPairs with off-white walls, light stone, warm fabrics and natural wood furniture.\nVisually lifts and cleans up the space, especially in natural daylight.\nGreat as a background wall finish: easy care and consistently neat on large planes.\nRecommended styles: Scandinavian, Japandi, modern minimal, light neo-classic.',
      uz: 'Krem ohangli och klen teksturasi — yorug‘, lekin “sovuq” emas.\nKvartira, yo‘lak, mehmonxona xonasi va ofis zonalarida juda mos.\nSut rang devorlar, och tosh, iliq matolar va tabiiy yog‘och mebel bilan uyg‘un.\nKunduzgi yoritishda xonani balandroq va “toza” ko‘rsatadi.\nFon devor sifatida amaliy: parvarishi oson, katta yuzada ham tartibli ko‘rinadi.\nUslub tavsiyasi: skandi, Japandi, zamonaviy minimalizm, yengil neoklassika.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FIvoryMaple01__d638b178.png?alt=media&token=94f3766f-865b-435c-a1d3-b684280b83db',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FIvoryMaple02__433dffd5.png?alt=media&token=f20350a5-3556-44d5-aace-ca0f790fd60a',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FIvoryMaple03__86851c39.png?alt=media&token=ca71f0fc-aab7-43ae-be52-256d89dfde6d'
    ]
  },
  {
    id: 'spc-panel-7',
    name: 'Taupe Oak',
    slug: 'taupe-oak-wd-007',
    series: 'SPC PANELS',
    code: 'WD-007',
    groupType: 'WD',
    colorTag: 'grey',
    shortDescription: {
      ru: 'Тёплый серо-тауповый дуб — универсальный выбор.',
      en: 'Warm taupe oak — a versatile, sophisticated choice.',
      uz: 'Iliq kulrang-taup dub — ko‘p qirrali, nafis tanlov.'
    },
    fullDescription: {
      ru: 'Сбалансированный оттенок между серым и бежевым — легко сочетается с любыми цветами мебели.\nИдеален для гостиных, спален, кабинетов и зон ожидания.\nСоздаёт ощущение спокойствия и уюта, не перегружая интерьер.\nХорошо работает с натуральными материалами: дерево, камень, лён.\nПрактичен для акцентных и фоновых стен: прост в уходе, сохраняет аккуратный вид.\nРекомендованные стили: современный, Japandi, неоклассика, контемпорари.',
      en: 'A balanced shade between grey and beige — pairs effortlessly with any furniture color.\nIdeal for living rooms, bedrooms, home offices and waiting areas.\nCreates a sense of calm and coziness without overwhelming the space.\nWorks well with natural materials: wood, stone, linen.\nPractical for both feature and background walls: easy to clean and maintains a neat appearance.\nRecommended styles: modern, Japandi, neo-classic, contemporary.',
      uz: 'Kulrang va bej orasidagi muvozanatli rang — har qanday mebel rangi bilan osongina uyg‘unlashadi.\nMehmonxona, yotoqxona, kabinet va kutish zallari uchun ideal.\nXonani ortiqcha yuklamasdan, sokinlik va qulaylik hissini beradi.\nTabiiy materiallar bilan yaxshi ishlaydi: yog‘och, tosh, zig‘ir.\nAksent va fon devorlari uchun amaliy: parvarishi oson, tartibli ko‘rinishini saqlaydi.\nUslub tavsiyasi: zamonaviy, Japandi, neoklassika, kontemporari.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FTaupeOak01__6c7e4eaa.png?alt=media&token=fa5b8640-e499-457f-86dd-1b5276f1f40d',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FTaupeOak03__9a673332.png?alt=media&token=e1081798-328a-473c-af50-93aefcf8e16b',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FTaupeOak02__bf88af2e.png?alt=media&token=650a7f68-0f93-4932-9702-41dbebb8c621'
    ]
  },
  {
    id: 'spc-panel-8',
    name: 'Sand Oak',
    slug: 'sand-oak-wd-008',
    series: 'SPC PANELS',
    code: 'WD-008',
    groupType: 'WD',
    colorTag: 'light',
    shortDescription: {
      ru: 'Песочно-светлый дуб для светлых пространств.',
      en: 'Sandy light oak — bright and airy for spacious interiors.',
      uz: 'Qum rangli och dub — keng va yorug‘ makonlar uchun.'
    },
    fullDescription: {
      ru: 'Нейтральный тёплый светлый тон. Подходит для больших площадей и мягких интерьерных решений.\nИдеален для гостиных, кухонь, детских комнат и спален.\nСочетается с белыми, бежевыми, серыми стенами и натуральным текстилем.\nСоздаёт ощущение лёгкости и простора, визуально расширяет комнату.\nПрактичен для фоновых стен: прост в уходе, не требует сложного сочетания с мебелью.\nРекомендованные стили: сканди, минимализм, современный, Japandi.',
      en: 'A neutral warm light tone. Perfect for large areas and soft interior schemes.\nIdeal for living rooms, kitchens, children\'s rooms and bedrooms.\nPairs with white, beige, grey walls and natural textiles.\nCreates a sense of lightness and space, visually expanding the room.\nPractical for background walls: easy to maintain and requires minimal coordination with furniture.\nRecommended styles: Scandinavian, minimal, modern, Japandi.',
      uz: 'Neytral iliq och rang. Katta maydonlar va yumshoq interyerlar uchun ideal.\nMehmonxona, oshxona, bolalar xonasi va yotoqxonalar uchun mos.\nOq, bej, kulrang devorlar va tabiiy matolar bilan uyg‘un.\nEngillik va kenglik hissini beradi, xonani vizual ravishda kattalashtiradi.\nFon devorlari uchun amaliy: parvarishi oson, mebel bilan murakkab uyg‘unlik talab qilmaydi.\nUslub tavsiyasi: skandi, minimalizm, zamonaviy, Japandi.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FSandOak01__01e5532f.png?alt=media&token=5de06fc6-07ce-4f86-9a38-8b622a02be65',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FSandOak02__9e30aedf.png?alt=media&token=545d9860-65e3-4b00-9f5f-415449d6a08e',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FSandOak03__29c81f11.png?alt=media&token=f9060aec-cfd9-4a99-81b3-225776fd4b69'
    ]
  },
  {
    id: 'spc-panel-9',
    name: 'Honey Oak',
    slug: 'honey-oak-wd-009',
    series: 'SPC PANELS',
    code: 'WD-009',
    groupType: 'WD',
    colorTag: 'medium',
    shortDescription: {
      ru: 'Медовый дуб — уют и тепло в деталях.',
      en: 'Honey oak — warm, inviting and full of natural character.',
      uz: 'Asal rangli dub — iliqlik va qulaylik.'
    },
    fullDescription: {
      ru: 'Тёплый золотистый оттенок древесины для жилых интерьеров, кафе и шоурумов.\nИдеален для создания уютной атмосферы в гостиной, спальне или домашнем кабинете.\nСочетается с бежевыми, кремовыми, терракотовыми оттенками и натуральными материалами.\nПридаёт пространству мягкость и тепло, не делая его тёмным.\nПрактичен для акцентных стен: легко ухаживать, выглядит благородно.\nРекомендодованные стили: современная классика, кантри, Japandi, уютный минимализм.',
      en: 'A warm golden wood tone for residential interiors, cafes and showrooms.\nPerfect for creating a cozy atmosphere in living rooms, bedrooms or home offices.\nPairs with beige, cream, terracotta tones and natural materials.\nAdds softness and warmth to a space without making it feel dark.\nPractical for feature walls: easy to maintain and looks sophisticated.\nRecommended styles: modern classic, country, Japandi, cozy minimal.',
      uz: 'Uy-ro‘zg‘or interyerlari, kafelar va shourumlar uchun iliq oltin rangli yog‘och toni.\nMehmonxona, yotoqxona yoki uy kabinetida qulay muhit yaratish uchun ideal.\nBej, krem, terrakota ranglari va tabiiy materiallar bilan uyg‘un.\nXonaga yumshoqlik va iliqlik baxsh etadi, lekin uni qorong‘i qilmaydi.\nAksent devorlar uchun amaliy: parvarishi oson, nafis ko‘rinadi.\nUslub tavsiyasi: zamonaviy klassika, kantri, Japandi, qulay minimalizm.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FHoneyOak01__aab4eade.png?alt=media&token=50dde638-7767-477c-b9ca-d6d5de10048d',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FHoneyOak02__f19a70e6.png?alt=media&token=a68a753f-9e7d-4686-aaa0-812186ed6094',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FHoneyOak03__5eb350bd.png?alt=media&token=d5d6a8e0-9f89-4abc-ae33-9b7dcc7829be'
    ]
  },
  {
    id: 'spc-panel-10',
    name: 'Mocha Walnut',
    slug: 'mocha-walnut-wd-010',
    series: 'SPC PANELS',
    code: 'WD-010',
    groupType: 'WD',
    colorTag: 'dark',
    shortDescription: {
      ru: 'Тёмный мокко-орех для акцентных композиций.',
      en: 'Dark mocha walnut — rich and dramatic for bold accent walls.',
      uz: 'To‘q mokko yong‘oq — aksentli kompozitsiyalar uchun.'
    },
    fullDescription: {
      ru: 'Глубокий коричневый тон, который делает стену «дороже» визуально.\nИдеален для создания контрастных зон в гостиной, кабинете или холле.\nСочетается с тёплым светом, латунью, чёрными деталями и светлыми, нейтральными поверхностями.\nПридаёт интерьеру глубину и характер, не делая его мрачным.\nПрактичен для акцентных стен: уход простой, поверхность выглядит аккуратно.\nРекомендодованные стили: современный, лофт, ар-деко, контемпорари.',
      en: 'A deep brown tone that visually elevates the wall, adding a sense of luxury.\nIdeal for creating contrasting zones in living rooms, studies or hallways.\nPairs with warm lighting, brass, black details and light, neutral surfaces.\nAdds depth and character to an interior without making it feel somber.\nPractical for feature walls: easy to maintain and maintains a neat appearance.\nRecommended styles: modern, loft, art deco, contemporary.',
      uz: 'Chuqur jigarrang ton — devorni vizual qimmatroq ko‘rsatadi.\nAksentli kompozitsiyalar uchun ideal.\nIliq yoritish, latun, qora detallar va yorug‘, neytral yuzalar bilan moslashadi.\nXonaga chuqurlik va xarakter baxsh etadi, lekin uni zerikarli qilmaydi.\nAksent devorlari uchun amaliy: parvarishi oson, tartibli ko‘rinadi.\nUslub tavsiyasi: zamonaviy, loft, art-deko, kontemporari.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FMochaWalnut01__f6ea988a.png?alt=media&token=fe215604-c6d9-4a4e-955f-98fe5d6577b1',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FMochaWalnut02__51337f1d.png?alt=media&token=fa27349b-4821-479e-b16a-d3eadb2d6838',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FMochaWalnut03__dfc2fc1a.png?alt=media&token=1e06b9b2-201e-44b0-8b76-de076abdbfbf'
    ]
  },
  {
    id: 'spc-panel-11',
    name: 'Obsidian Topo',
    slug: 'obsidian-topo-stp-001',
    series: 'SPC PANELS',
    code: 'STP-001',
    groupType: 'STP',
    colorTag: 'dark',
    shortDescription: {
      ru: 'Тёмный камень с графикой «топография».',
      en: 'Dark stone with a topographic pattern.',
      uz: 'Topografik naqshli to‘q rangli tosh.'
    },
    fullDescription: {
      ru: 'Темный «каменный» декор с тонкими линиями. Эффектно смотрится в лофте и техно-интерьерах.\nИдеален для акцентных стен, кухонных фартуков и зон с повышенной проходимостью.\nСочетается с матовым чёрным металлом, стеклом, бетоном и яркими акцентами.\nПридаёт пространству глубину и современный характер.\nПрактичен в уходе: поверхность легко очищается и устойчива к истиранию.\nРекомендодованные стили: лофт, техно, индустриальный минимализм, хай-тек.',
      en: 'A dark stone decor with fine topographic lines. Looks striking in loft and techno interiors.\nIdeal for accent walls, kitchen backsplashes and high-traffic areas.\nPairs with matte black metal, glass, concrete and vibrant accents.\nAdds depth and a modern character to the space.\nPractical for maintenance: the surface is easy to clean and resistant to abrasion.\nRecommended styles: loft, techno, industrial minimalism, high-tech.',
      uz: 'Mayda topo naqshli to‘q rangli «tosh» dekor. Loft va texno interyerlarda ta’sirchan ko‘rinadi.\nAksent devorlari, oshxona apronlari va ko‘p yuriladigan joylar uchun ideal.\nMat qora metall, shisha, beton va yorqin aksentlar bilan moslashadi.\nXonaga chuqurlik va zamonaviy xarakter baxsh etadi.\nParvarishi oson: yuzasi oson tozalanadi va ishqalanishga chidamli.\nUslub tavsiyasi: loft, texno, industrial minimalizm, high-tech.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FObsidianTopo01__eb497efb.png?alt=media&token=87f5698c-f3e7-4f06-b04e-459878aa6d2d',
    images: [
      '/media/ObsidianTopo02.png',
      '/media/ObsidianTopo03.png'
    ]
  },
  {
    id: 'spc-panel-12',
    name: 'Basalt Topo',
    slug: 'basalt-topo-stp-002',
    series: 'SPC PANELS',
    code: 'STP-002',
    groupType: 'STP',
    colorTag: 'dark',
    shortDescription: {
      ru: 'Ещё более строгий тёмный топо-декор.',
      en: 'An even more intense dark topographic decor.',
      uz: 'Yanada qat’iyroq to‘q topo-dekor.'
    },
    fullDescription: {
      ru: 'Вариация тёмного топо-паттерна с более плотным рисунком для выразительных зон.\nИдеален для создания драматического эффекта в интерьере.\nСочетается с матовыми чёрными элементами, тёмным деревом и яркими акцентами.\nПодчеркивает архитектуру пространства, добавляя ему глубины и характера.\nПрактичен для зон с высокой проходимостью: устойчив к царапинам и легко моется.\nРекомендодованные стили: индустриальный лофт, техно, современный минимализм.',
      en: 'A variation of the dark topo pattern with a denser design for dramatic zones.\nIdeal for creating a dramatic effect in interiors.\nPairs with matte black elements, dark wood, and vibrant accents.\nEmphasizes the architecture of the space, adding depth and character.\nPractical for high-traffic areas: resistant to scratches and easy to clean.\nRecommended styles: industrial loft, techno, modern minimalism.',
      uz: 'Zichroq naqshli to‘q topo-pattern varianti — ta’sirchan zonalarni yaratish uchun.\nXonada dramatik effekt yaratish uchun ideal.\nMat qora elementlar, to‘q yog‘och va yorqin aksentlar bilan moslashadi.\nXonaning arxitekturasini ta’kidlaydi, chuqurlik va xarakter qo‘shadi.\nKo‘p yuriladigan joylar uchun amaliy: tirnalishlarga chidamli va oson tozalanadi.\nUslub tavsiyasi: industrial loft, texno, zamonaviy minimalizm.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FBasaltTopo01__8408c271.png?alt=media&token=e39554b7-d42c-4baf-9b75-903a86006631',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FBasaltTopo02__b30022a9.png?alt=media&token=a77bd4fe-83e7-4a96-9498-015af9bc21f2',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FBasaltTopo03__846c23a8.png?alt=media&token=9eebff6d-5bdc-4110-9530-49138474fd31'
    ]
  },
  {
    id: 'spc-panel-13',
    name: 'Bronze Topo',
    slug: 'bronze-topo-stp-003',
    series: 'SPC PANELS',
    code: 'STP-003',
    groupType: 'STP',
    colorTag: 'brown',
    shortDescription: {
      ru: 'Тёплый камень с топографическим характером.',
      en: 'Warm stone with a topographic character.',
      uz: 'Topografik xarakterli iliq tosh.'
    },
    fullDescription: {
      ru: 'Коричнево-бронзовый тон с графическим паттерном, хорошо сочетается с тёплой подсветкой.\nИдеален для создания уютной и одновременно современной атмосферы.\nСочетается с тёплым деревом, латунью, бронзой и нейтральными оттенками.\nПридаёт интерьеру глубину и изысканность, не перегружая его.\nПрактичен для акцентных стен и зон с умеренной проходимостью.\nРекомендодованные стили: современный, Japandi, неоклассика, контемпорари.',
      en: 'A brown-bronze tone with a graphic pattern, pairs well with warm lighting.\nIdeal for creating a cozy yet modern atmosphere.\nPairs with warm wood, brass, bronze and neutral tones.\nAdds depth and sophistication to the interior without overwhelming it.\nPractical for accent walls and areas with moderate traffic.\nRecommended styles: modern, Japandi, neo-classic, contemporary.',
      uz: 'Grafik naqshli jigarrang-bronza toni, iliq yoritish bilan yaxshi uyg‘unlashadi.\nQulay va shu bilan birga zamonaviy atmosferani yaratish uchun ideal.\nIliq yog‘och, latun, bronza va neytral ranglar bilan moslashadi.\nXonaga chuqurlik va nafosat baxsh etadi, uni ortiqcha yuklamaydi.\nAksent devorlari va o‘rtacha harakatlanish joylari uchun amaliy.\nUslub tavsiyasi: zamonaviy, Japandi, neoklassika, kontemporari.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: '/media/BronzeTopo01_new.png',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FBronzeTopo02__a69e3c7a.png?alt=media&token=c4d9d49c-fab9-44eb-aec6-419cab78d6ce',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FBronzeTopo03__e808bfda.png?alt=media&token=6a88a4d3-7bac-4a91-98d4-6c12bb16f6b5'
    ]
  },
  {
    id: 'spc-panel-14',
    name: 'Carrara Vein',
    slug: 'carrara-vein-stp-004',
    series: 'SPC PANELS',
    code: 'STP-004',
    groupType: 'STP',
    colorTag: 'white',
    shortDescription: {
      ru: 'Светлый мрамор с деликатной прожилкой.',
      en: 'Light marble with a delicate vein.',
      uz: 'Nozik tomirli och marmar.'
    },
    fullDescription: {
      ru: 'Белый каменный декор с серыми прожилками — универсальная классика для кухни/санузла/холла.\nИдеален для создания светлого, чистого и элегантного пространства.\nСочетается с белыми, серыми, бежевыми стенами, чёрной фурнитурой и тёплым деревом.\nПридаёт интерьеру ощущение простора и лёгкости, визуально расширяет комнату.\nПрактичен для кухонь, ванных комнат, коридоров и зон с высокой проходимостью.\nРекомендодованные стили: современная классика, минимализм, неоклассика, контемпорари.',
      en: 'White stone decor with grey veins — a versatile classic for kitchens, bathrooms, and hallways.\nIdeal for creating a bright, clean, and elegant space.\nPairs with white, grey, beige walls, black hardware, and warm wood.\nAdds a sense of spaciousness and lightness to the interior, visually expanding the room.\nPractical for kitchens, bathrooms, corridors, and high-traffic areas.\nRecommended styles: modern classic, minimalism, neo-classic, contemporary.',
      uz: 'Kulrang tomirli oq tosh dekor — oshxona/sanim/xoll uchun ko‘p qirrali klassika.\nAyniqsa yorug‘, toza va nafis makon yaratish uchun ideal.\nOq, kulrang, bej devorlar, qora furnitura va iliq yog‘och bilan moslashadi.\nXonaga kenglik va yengillik hissini beradi, vizual ravishda xonani kattalashtiradi.\nOshxona, sanim, yo‘lak va ko‘p yuriladigan joylar uchun amaliy.\nUslub tavsiyasi: zamonaviy klassika, minimalizm, neoklassika, kontemporari.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FCarraraVein10__6fa65b69.png?alt=media&token=2bd4a5ca-7088-4c2d-9c78-771b5a01af67',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FCarraraVein03__5c4e4d16.png?alt=media&token=719dc648-01b8-4284-a43a-2366b90b7dec',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FCarraraVein10__6fa65b69.png?alt=media&token=e7c6e792-8c29-4ca0-9c75-ad73b4c81be7'
    ]
  },
  {
    id: 'spc-16',
    name: 'Silver Marble',
    slug: 'silver-marble-stp-005',
    series: 'SPC PANELS',
    code: 'STP-005',
    groupType: 'STP',
    colorTag: 'grey',
    shortDescription: {
      ru: 'Серый мрамор с динамичным рисунком.',
      en: 'Grey marble with a dynamic pattern.',
      uz: 'Dinamik naqshli kulrang marmar.'
    },
    fullDescription: {
      ru: 'Холодный серо-мраморный рисунок для современного минимализма.\nИдеален для создания элегантного и сдержанного интерьера.\nСочетается с чёрными, белыми, серыми стенами, металлом и стеклом.\nПридаёт пространству ощущение прохлады и утончённости.\nПрактичен для ванных комнат, кухонь и зон с умеренной проходимостью.\nРекомендодованные стили: современный минимализм, хай-тек, лофт, контемпорари.',
      en: 'A cool grey marble pattern for modern minimalism.\nIdeal for creating an elegant and understated interior.\nPairs with black, white, grey walls, metal, and glass.\nAdds a sense of coolness and sophistication to the space.\nPractical for bathrooms, kitchens, and areas with moderate traffic.\nRecommended styles: modern minimalism, high-tech, loft, contemporary.',
      uz: 'Zamonaviy minimalizm uchun salqin kulrang marmar naqshi.\nNafis va kamtarona interyer yaratish uchun ideal.\nQora, oq, kulrang devorlar, metall va shisha bilan moslashadi.\nXonaga salqinlik va nafosat hissini beradi.\nSanimlar, oshxonalar va o‘rtacha harakatlanish joylari uchun amaliy.\nUslub tavsiyasi: zamonaviy minimalizm, high-tech, loft, kontemporari.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FSilverMarble01__fd1809bd.png?alt=media&token=0c65ebda-879b-4ffd-89e8-a330af7a2e18',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FSilverMarble02__e2959a6e.png?alt=media&token=1bf0f896-f10a-4edc-b8d3-2ab6afd9e872',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FSilverMarble03__aee5f995.png?alt=media&token=0c6aab9c-2f3c-4b14-99bf-db407a49554c'
    ]
  },
  {
    id: 'spc-17',
    name: 'Mist Concrete',
    slug: 'mist-concrete-stp-006',
    series: 'SPC PANELS',
    code: 'STP-006',
    groupType: 'STP',
    colorTag: 'light',
    shortDescription: {
      ru: 'Светлый бетон — нейтральная база.',
      en: 'Light concrete — a neutral, versatile base.',
      uz: 'Och rangli beton — neytral, ko‘p qirrali asos.'
    },
    fullDescription: {
      ru: 'Мягкий светло-серый «бетон» без агрессии. Отлично для больших стен.\nИдеален для создания спокойного и современного фона.\nСочетается с деревом, металлом, стеклом и любыми цветовыми акцентами.\nПридаёт пространству ощущение лёгкости и воздуха.\nПрактичен для фоновых стен: прост в уходе, хорошо смотрится на больших площадях.\nРекомендодованные стили: минимализм, сканди, лофт, контемпорари.',
      en: 'A soft, light grey «concrete» without harshness. Excellent for large walls.\nIdeal for creating a calm and modern backdrop.\nPairs with wood, metal, glass, and any color accents.\nAdds a sense of lightness and airiness to the space.\nPractical for background walls: easy to maintain and looks great on large surfaces.\nRecommended styles: minimalism, Scandinavian, loft, contemporary.',
      uz: 'Yumshoq, och kulrang «beton» — agressivsiz. Katta devorlar uchun ajoyib.\nXotirjam va zamonaviy fon yaratish uchun ideal.\nNoyob, metall, shisha va har qanday rang aksentlari bilan moslashadi.\nXonaga yengillik va havodorlik hissini beradi.\nFon devorlari uchun amaliy: parvarishi oson, katta yuzalarda chiroyli ko‘rinadi.\nUslub tavsiyasi: minimalizm, skandi, loft, kontemporari.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FMistConcrete01__c6d09b45.png?alt=media&token=aeaaf3b8-8e1d-46ac-aa7a-ec0489079baa',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FMistConcrete02__d9c72e84.png?alt=media&token=6775a250-41b0-41d3-b05d-0938a52b0541',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FMistConcrete03__2d582c90.png?alt=media&token=fbe1875a-f79e-41e7-a8b8-092089e613f5'
    ]
  },
  {
    id: 'spc-18',
    name: 'Graphite Concrete',
    slug: 'graphite-concrete-stp-007',
    series: 'SPC PANELS',
    code: 'STP-007',
    groupType: 'STP',
    colorTag: 'dark',
    shortDescription: {
      ru: 'Тёмный бетон для контраста и глубины.',
      en: 'Dark concrete for contrast and depth.',
      uz: 'Kontrast va chuqurlik uchun to‘q beton.'
    },
    fullDescription: {
      ru: 'Тёмно-серый бетонный декор для акцентных стен и коммерческих пространств.\nИдеален для создания смелого и современного интерьера.\nСочетается с чёрным металлом, тёмным деревом, стеклом и яркими акцентами.\nПридаёт пространству индустриальный характер и визуальную глубину.\nПрактичен для зон с высокой проходимостью: устойчив к истиранию и легко моется.\nРекомендодованные стили: лофт, индустриальный, техно, современный минимализм.',
      en: 'Dark grey concrete decor for accent walls and commercial spaces.\nIdeal for creating a bold and modern interior.\nPairs with black metal, dark wood, glass, and vibrant accents.\nAdds an industrial character and visual depth to the space.\nPractical for high-traffic areas: resistant to abrasion and easy to clean.\nRecommended styles: loft, industrial, techno, modern minimalism.',
      uz: 'Aksent devorlari va tijorat joylari uchun to‘q kulrang beton dekor.\nJasur va zamonaviy interyer yaratish uchun ideal.\nQora metall, to‘q yog‘och, shisha va yorqin aksentlar bilan moslashadi.\nXonaga industrial xarakter va vizual chuqurlik baxsh etadi.\nKo‘p yuriladigan joylar uchun amaliy: ishqalanishga chidamli va oson tozalanadi.\nUslub tavsiyasi: loft, industrial, texno, zamonaviy minimalizm.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FGraphiteConcrete01__a8654bb1.png?alt=media&token=f58f5441-fb08-4c97-a002-7656673ee874',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FGraphiteConcrete02__efa2e911.png?alt=media&token=36734d1d-ae5b-4154-8ea5-57d926e2d93f',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FGraphiteConcrete03__910523cd.png?alt=media&token=771ac47c-a5fa-46ae-9935-d2a5880cc35b'
    ]
  },
  {
    id: 'spc-panel-18',
    name: 'Blue Slate',
    slug: 'blue-slate-stp-008',
    series: 'SPC PANELS',
    code: 'STP-008',
    groupType: 'STP',
    colorTag: 'bluegrey',
    shortDescription: {
      ru: 'Сланец с холодным сине-серым оттенком.',
      en: 'Slate with a cool blue-grey hue.',
      uz: 'Salqin ko‘k-kulrang tusli slanets.'
    },
    fullDescription: {
      ru: 'Сдержанный сине-серый камень, хорошо сочетается с белыми и древесными элементами.\nИдеален для создания элегантного и современного интерьера.\nСочетается с белыми, серыми, бежевыми стенами, натуральным деревом и металлом.\nПридаёт пространству ощущение прохлады и утончённости.\nПрактичен для ванных комнат, кухонь и зон с умеренной проходимостью.\nРекомендодованные стили: современный минимализм, сканди, лофт, контемпорари.',
      en: 'A subdued blue-grey stone, pairs well with white and wood elements.\nIdeal for creating an elegant and modern interior.\nPairs with white, grey, beige walls, natural wood, and metal.\nAdds a sense of coolness and sophistication to the space.\nPractical for bathrooms, kitchens, and areas with moderate traffic.\nRecommended styles: modern minimalism, Scandinavian, loft, contemporary.',
      uz: 'Och ko‘k-kulrang tosh, oq va yog‘och elementlari bilan yaxshi uyg‘unlashadi.\nNafis va zamonaviy interyer yaratish uchun ideal.\nOq, kulrang, bej devorlar, tabiiy yog‘och va metall bilan moslashadi.\nXonaga salqinlik va nafosat hissini beradi.\nSanimlar, oshxonalar va o‘rtacha harakatlanish joylari uchun amaliy.\nUslub tavsiyasi: zamonaviy minimalizm, skandi, loft, kontemporari.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: '/media/BlueSlate01.png',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FBlueSlate02__de78ba26.png?alt=media&token=dcc85a69-494c-4210-898c-9617e9c97663',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FxM0Mnz45BodkpKKfrWpD1Ko6SME2%2FBlueSlate03__1b64fd32.png?alt=media&token=37840714-f674-4b8a-8d97-03369319ff45'
    ]
  },
  {
    id: 'spc-panel-19',
    name: 'Pearl Stone',
    slug: 'pearl-stone-stp-009',
    series: 'SPC PANELS',
    code: 'STP-009',
    groupType: 'STP',
    colorTag: 'white',
    shortDescription: {
      ru: 'Светлый камень с мягкой текстурой.',
      en: 'Light stone with a soft texture.',
      uz: 'Yumshoq teksturali och rangli tosh.'
    },
    fullDescription: {
      ru: 'Очень светлый каменный декор для «чистых» интерьеров и светлых коридоров.\nИдеален для создания максимально светлого и воздушного пространства.\nСочетается с белыми, пастельными, серыми стенами и натуральным деревом.\nПридаёт интерьеру ощущение свежести и простора.\nПрактичен для фоновых стен: прост в уходе, хорошо смотрится на больших площадях.\nРекомендодованные стили: минимализм, сканди, Japandi, современный.',
      en: 'A very light stone decor for «clean» interiors and bright corridors.\nIdeal for creating the brightest and airiest space possible.\nPairs with white, pastel, grey walls and natural wood.\nAdds a sense of freshness and spaciousness to the interior.\nPractical for background walls: easy to maintain and looks great on large surfaces.\nRecommended styles: minimalism, Scandinavian, Japandi, modern.',
      uz: '«Toza» interyerlar va yorug‘ yo‘laklar uchun juda och rangli tosh dekor.\nImkon qadar eng yorug‘ va havodor makon yaratish uchun ideal.\nOq, pastel, kulrang devorlar va tabiiy yog‘och bilan moslashadi.\nXonaga yangilik va kenglik hissini beradi.\nFon devorlari uchun amaliy: parvarishi oson, katta yuzalarda chiroyli ko‘rinadi.\nUslub tavsiyasi: minimalizm, skandi, Japandi, zamonaviy.'
    },
    specs: { thickness: '2.5–8.0 mm', width: '600 / 98 / 120 mm', height: '2400 mm' },
    image: '/media/PearlStone01.png',
    images: ['/media/PearlStone02.png', '/media/PearlStone03.png']
  },
  // CARPET TILE - 20 Products
  {
    id: 'pct-0352',
    name: { ru: 'Espresso Dark', uz: 'Espresso Dark', en: 'Espresso Dark' },
    slug: 'espresso-dark-pct-0352',
    series: 'CARPET TILE',
    collection: 'gravity',
    code: 'PCT_0352',
    shortDescription: {
      ru: 'Глубокий тёмный тон для статусных офисов и камерных зон.',
      en: 'A deep dark tone for executive offices and intimate areas.',
      uz: 'Statusli ofislar va sokin zonalar uchun chuqur to‘q ohang.'
    },
    fullDescription: {
      ru: 'Gravity в тоне Espresso Dark — это «каменная» глубина с городским ритмом и мягким рельефом.\nПоверхность выглядит собранно и дорого: тёмный фон скрывает мелкие следы повседневной нагрузки.\nИдеально для кабинетов, переговорных, коридоров, ресепшенов и коммерческих холлов.\nСочетается с металлом и стеклом, тёмным деревом, графитовым камнем; красиво раскрывается в тёплом и нейтральном свете.\nУход простой: регулярная сухая чистка и точечная влажная уборка — и покрытие остаётся аккуратным.\nНастроение: деловое, премиальное, уверенное.',
      en: 'Espresso Dark from Gravity brings stone-like depth with an urban rhythm and a soft, tactile relief.\nIt reads composed and premium: the dark base helps hide minor marks from daily traffic.\nBest for executive offices, meeting rooms, corridors, reception areas, and commercial lobbies.\nPairs with metal and glass, dark wood, and graphite stone; looks especially rich in warm or neutral lighting.\nEasy to maintain: routine vacuuming and occasional damp cleaning keep it crisp.\nMood: business, premium, confident.',
      uz: 'Gravity kolleksiyasidagi Espresso Dark — shahar ritmi va yumshoq relyefga ega “tosh” chuqurligi.\nKo‘rinishi yig‘ilgan va premium: to‘q fon kundalik izlarni kamroq ko‘rsatadi.\nKabinet, muzokara xonasi, yo‘lak, resepshen va tijorat hollari uchun ayni muddao.\nMetall va shisha, to‘q yog‘och hamda grafit tosh bilan uyg‘unlashadi; iliq yoki neytral yoritishda yanada boy ko‘rinadi.\nParvarishi oson: muntazam changyutgich va zarur paytda nam tozalash yetarli.\nKayfiyat: ishbilarmon, premium, ishonchli.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FludOJwATJSVlW062ubN48fVuR6D3%2FEspressoDark01__c7825913.png?alt=media&token=9e317e01-41e7-48ea-82e0-4a0c38772cce',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FEspressoDark02__5a1d1d8c.png?alt=media&token=f06b54be-7b10-47ac-81db-77a800a0c5ba',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FEspressoDark03__5aacefc8.png?alt=media&token=ce10098b-aeb6-4ee9-ac4d-e6631fce2614'
    ]
  },
  {
    id: 'pct-1253',
    name: { ru: 'Midnight Blue', uz: 'Midnight Blue', en: 'Midnight Blue' },
    slug: 'midnight-blue-pct-1253',
    series: 'CARPET TILE',
    collection: 'gravity',
    code: 'PCT_1253',
    shortDescription: {
      ru: 'Холодная глубина синего — для технологичных и строгих интерьеров.',
      en: 'A cool blue depth for tech-minded, structured interiors.',
      uz: 'Texnologik va tartibli interyerlar uchun sovuq ko‘k chuqurlik.'
    },
    fullDescription: {
      ru: 'Midnight Blue в Gravity — тёмно-синий акцент с мягким переходом оттенков и визуальной устойчивостью.\nРельеф не кричит, но создаёт глубину — пространство выглядит более собранным.\nПодходит для open-space офисов, переговорных, коворкингов, коридоров и общественных зон.\nЛучшие сочетания: светлый камень, алюминий, чёрная фурнитура, холодный или нейтральный свет.\nВысокопрактично: плитка сохраняет опрятный вид при регулярной чистке и не требует сложного ухода.\nНастроение: спокойная строгость, современный премиум.',
      en: 'Midnight Blue in Gravity is a deep blue with smooth tonal transitions and visual stability.\nThe texture is subtle yet adds depth, making the space feel disciplined and calm.\nGreat for open-space offices, meeting rooms, coworking areas, corridors, and public zones.\nBest pairings: light stone, aluminum, black hardware, and cool-to-neutral lighting.\nHighly practical: it stays neat with regular vacuuming and simple spot cleaning.\nMood: calm strictness, modern premium.',
      uz: 'Gravity’dagi Midnight Blue — silliq o‘tishlarga ega chuqur ko‘k ohang, ko‘rinish jihatdan juda barqaror.\nFaktura sokin, lekin chuqurlik beradi — makon tartibli ko‘rinadi.\nOpen-space ofislar, muzokara xonalari, kovinglar, yo‘laklar va jamoat zonalari uchun mos.\nYaxshi uyg‘unliklar: och tosh, alyuminiy, qora furnitura, sovuq yoki neytral yoritish.\nAmaliy: muntazam tozalash bilan uzun vaqt ozoda ko‘rinishini saqlaydi.\nKayfiyat: sokin qat’iyat, zamonaviy premium.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FludOJwATJSVlW062ubN48fVuR6D3%2FMidnightBlue01__770009cd.png?alt=media&token=0195bbd3-19a5-47e1-a6a3-f106b08b81bb',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FMidnightBlue02__995fad4d.png?alt=media&token=132215ff-f002-4a14-b224-6011f216ceca',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FMidnightBlue03__4e008496.png?alt=media&token=e34a702e-53dd-4f20-90d1-f5825fcafb1f'
    ]
  },
  {
    id: 'pct-1469',
    name: { ru: 'Basalt Mist', uz: 'Basalt Mist', en: 'Basalt Mist' },
    slug: 'basalt-mist-pct-1469',
    series: 'CARPET TILE',
    collection: 'gravity',
    code: 'PCT_1469',
    shortDescription: {
      ru: 'Серый «базальт» с дымкой — спокойная основа для больших площадей.',
      en: 'A misty basalt grey — a calm base for large floor areas.',
      uz: 'Tumanli bazalt kulrang — katta maydonlar uchun sokin asos.'
    },
    fullDescription: {
      ru: 'Basalt Mist — фирменная «каменная» база Gravity: плавные переходы и мягкий визуальный рельеф.\nДекор не спорит с интерьером, но добавляет глубину и ощущение устойчивости.\nОтличен для коридоров, холлов, зон ожидания, коммерции и аккуратных домашних пространств.\nЛегко дружит с деревом (тёплым и холодным), бетоном и металлом; performs best under neutral lighting.\nА practical shade: daily cleaning keeps it looking even and tidy.\nMood: composed, calm, professional.',
      en: 'Basalt Mist is a signature Gravity base: smooth transitions and a soft visual relief with a stone-like character.\nIt doesn’t fight the interior, yet adds depth and a sense of stability.\nPerfect for corridors, lobbies, waiting areas, commercial spaces, and refined home use.\nPairs easily with warm or cool wood, concrete, and metal; performs best under neutral lighting.\nA practical shade: daily cleaning keeps it looking even and tidy.\nMood: composed, calm, professional.',
      uz: 'Basalt Mist — Gravity’ning “tosh” xarakterli asosiy dekori: silliq o‘tishlar va yumshoq relyef.\nInteryer bilan bahslashmaydi, lekin chuqurlik va barqarorlik hissini beradi.\nYo‘lak, holl, kutish zonasi, tijorat maydonlari va ozoda uy interyerlari uchun juda mos.\nIliq yoki sovuq yog‘och, beton va metall bilan oson uyg‘unlashadi; neytral yoritishda ayniqsa yaxshi ko‘rinadi.\nAmaliy rang: kundalik tozalash bilan silliq va tartibli ko‘rinishini saqlaydi.\nKayfiyat: yig‘ilgan, sokin, professional.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: '/media/BasaltMist01.png',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FBasaltMist02__ea53c7c7.png?alt=media&token=c7190ba0-5864-4ea9-8818-acb41cd45dc0',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FBasaltMist03__61a2c1eb.png?alt=media&token=e9bedae3-4281-4983-aa63-ec2b0da03180'
    ]
  },
  {
    id: 'pct-3169',
    name: { ru: 'Moss Field', uz: 'Moss Field', en: 'Moss Field' },
    slug: 'moss-field-pct-3169',
    series: 'CARPET TILE',
    collection: 'gravity',
    code: 'PCT_3169',
    shortDescription: {
      ru: 'Глубокий зелёный оттенок — мягкая «природа» в деловом пространстве.',
      en: 'A deep green tone — a soft touch of nature in business spaces.',
      uz: 'Chuqur yashil ohang — ish makonida yumshoq “tabiat” hissi.'
    },
    fullDescription: {
      ru: 'Moss Field в Gravity добавляет интерьеру спокойную природную ноту, сохраняя индустриальную собранность.\nФактура читается мягко: без пестроты, с ощущением глубины и рельефа.\nХорош для кабинетов, зон ожидания, переговорных, гостиничных коридоров и домашних рабочих пространств.\nСочетается с тёплым деревом, матовым металлом, бежевыми и графитовыми стенами; особенно красиво в тёплом свете.\nПрактичен в уходе: ежедневная чистка поддерживает аккуратный вид без «вылизывания».\nНастроение: спокойствие, уверенность, премиальный комфорт.',
      en: 'Moss Field in Gravity brings a calm natural note while keeping an industrial, composed feel.\nThe texture reads soft and refined — no visual noise, just depth and relief.\nIdeal for offices, waiting areas, meeting rooms, hotel corridors, and home workspaces.\nPairs with warm wood, matte metal, beige and graphite walls; looks best in warm lighting.\nPractical care: regular cleaning keeps it neat without extra effort.\nMood: calm, confident, premium comfort.',
      uz: 'Gravity’dagi Moss Field interyerga sokin tabiiy ohang qo‘shadi, shu bilan birga industrial tartibni saqlaydi.\nFaktura yumshoq ko‘rinadi: ortiqcha rang-baranglik yo‘q, faqat chuqurlik va relyef.\nOfis, kutish zonasi, muzokara xonasi, mehmonxona yo‘laklari va uy ish burchagi uchun mos.\nIliq yog‘och, mat metall, bej va grafit devorlar bilan uyg‘un; iliq yoritishda ayniqsa chiroyli.\nParvarishi amaliy: muntazam tozalash ozoda ko‘rinishni ushlab turadi.\nKayfiyat: sokinlik, ishonch, premium qulaylik.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: '/media/MossField01.png',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FMossField02__80307d11.png?alt=media&token=7cd39d70-20d5-4113-af73-cbbce4a69538',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FMossField03__324ed84d.png?alt=media&token=60dac1b4-d57b-4aa2-94f4-151d96bf7c83'
    ]
  },
  {
    id: 'pct-5736',
    name: { ru: 'Bronze Dune', uz: 'Bronze Dune', en: 'Bronze Dune' },
    slug: 'bronze-dune-pct-5736',
    series: 'CARPET TILE',
    collection: 'gravity',
    code: 'PCT_5736',
    shortDescription: {
      ru: 'Тёплая бронза — для гостеприимных холлов и премиальной коммерции.',
      en: 'Warm bronze for welcoming lobbies and premium commercial spaces.',
      uz: 'Mehmondo‘st holl va premium tijorat uchun iliq bronza ohangi.'
    },
    fullDescription: {
      ru: 'Bronze Dune раскрывает идею Gravity через тёплый, «песочно-бронзовый» тон и мягкие переходы.\nДекор делает пространство более уютным, но остаётся индустриально собранным.\nПодходит для холлов, шоурумов, офисных лаунж-зон, бутик-ретейла и домашних гостиных.\nСочетается с тёплым деревом, латунью, светлым камнем и мягким освещением.\nПрактичность на каждый день: визуально держит чистоту, легко обслуживается стандартной уборкой.\nНастроение: тёплый премиум, уверенная гостеприимность.',
      en: 'Bronze Dune expresses Gravity through a warm, sandy-bronze tone with smooth transitions.\nIt makes spaces feel welcoming while staying industrial and composed.\nGreat for lobbies, showrooms, office lounges, boutique retail, and home living rooms.\nPairs with warm wood, brass accents, light stone, and soft lighting.\nPractical for everyday use: it keeps a clean look and is easy to maintain.\nMood: warm premium, confident hospitality.',
      uz: 'Bronze Dune Gravity g‘oyasini iliq qum‑bronza ohangi va silliq o‘tishlar orqali ochadi.\nMakonni yanada qulay qiladi, lekin industrial tartibni saqlab qoladi.\nHoll, shourum, ofis lounge zonalari, butik savdo va uy mehmonxonasi uchun mos.\nIliq yog‘och, latun aksentlari, och tosh va yumshoq yoritish bilan chiroyli uyg‘unlashadi.\nKundalik amaliy: toza ko‘rinishni ushlab turadi, oddiy tozalash yetarli.\nKayfiyat: iliq premium, ishonchli mehmondo‘stlik.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FBronzeDune01__166f95ef.png?alt=media&token=9b1197e7-52ef-44e5-a162-5466930c603e',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FBronzeDune02__e8128d78.png?alt=media&token=cd2c6edd-4a3b-46da-b618-e3ee50b2841f',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FBronzeDune03__735f3089.png?alt=media&token=a29bc455-3d1a-4481-b0f2-228b6506726a'
    ]
  },
  {
    id: 'pct-5741',
    name: { ru: 'Cinnamon Dune', uz: 'Cinnamon Dune', en: 'Cinnamon Dune' },
    slug: 'cinnamon-dune-pct-5741',
    series: 'CARPET TILE',
    collection: 'gravity',
    code: 'PCT_5741',
    shortDescription: {
      ru: 'Карамельно‑пряный нюанс — тёплый акцент без перегруза.',
      en: 'A spiced caramel nuance — a warm accent without visual overload.',
      uz: 'Ziravorli karamel ohangi — ortiqcha yuklamasiz iliq aksent.'
    },
    fullDescription: {
      ru: 'Cinnamon Dune — более выразительный тёплый тон в логике Gravity: плавная фактура и мягкий рельеф.\nОн «подсвечивает» пространство и делает его дружелюбнее, сохраняя премиальный порядок.\nРекомендуем для офисных зон отдыха, гостиничных холлов, переговорных и домашних гостиных.\nСочетается с натуральным деревом, тёплым камнем и матовым металлом; идеально под тёплый свет.\nВ эксплуатации практичен: держит аккуратность и легко чистится без сложной химии.\nНастроение: тёплый деловой комфорт, мягкая динамика.',
      en: 'Cinnamon Dune is a warmer, more expressive Gravity tone with smooth texture and gentle relief.\nIt subtly “lights up” the space, keeping a premium sense of order.\nRecommended for office lounge areas, hotel lobbies, meeting rooms, and home living spaces.\nPairs with natural wood, warm stone and matte metal; ideal under warm lighting.\nPractical in use: stays tidy and cleans easily without harsh chemicals.\nMood: warm business comfort, soft dynamics.',
      uz: 'Cinnamon Dune — Gravity uslubida iliqroq va ifodaliroq ton: silliq faktura va yumshoq relyef.\nMakonni yengil “yoritadi”, premium tartib hissini saqlab qoladi.\nOfis dam olish zonalari, mehmonxona hollari, muzokara xonalari va uy mehmonxonasi uchun tavsiya.\nTabiiy yog‘och, iliq tosh va mat metall bilan mos; iliq yoritishda ideal.\nAmaliy: ozoda ko‘rinishni ushlab turadi, murakkab kimyoviy vositalarsiz tozalanadi.\nKayfiyat: iliq ishbilarmon qulaylik, yumshoq dinamika.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: '/media/CinnamonDune01.png',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FCinnamonDune02__4f8f58d9.png?alt=media&token=10f7f944-2abb-4978-843b-f016b8b46b7f',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FCinnamonDune03__7eb522f5.png?alt=media&token=366f7c87-9329-4e90-b7e5-16be9f22a779'
    ]
  },
  {
    id: 'pct-9123',
    name: { ru: 'Silver Mist', uz: 'Silver Mist', en: 'Silver Mist' },
    slug: 'silver-mist-pct-9123',
    series: 'CARPET TILE',
    collection: 'gravity',
    code: 'PCT_9123',
    shortDescription: {
      ru: 'Светло‑серый туман — для чистой архитектуры и делового ритма.',
      en: 'A light grey mist for clean architecture and a business rhythm.',
      uz: 'Toza arxitektura va ish ritmi uchun och kulrang “tuman”.'
    },
    fullDescription: {
      ru: 'Silver Mist — светлая интерпретация Gravity: каменная глубина без тяжести и мягкие переходы оттенков.\nДекор визуально расширяет пространство и делает его светлее.\nПодходит для офисов, учебных и общественных зон, коридоров, переговорных и аккуратных квартир.\nСочетается с белыми и серыми стенами, светлым деревом, нержавейкой; хорошо работает в холодном и нейтральном свете.\nПрактичный вариант на каждый день: поддерживает опрятность и легко чистится.\nНастроение: собранность, свет, профессиональная спокойность.',
      en: 'Silver Mist is a lighter Gravity interpretation: stone-like depth without heaviness and smooth tonal transitions.\nIt visually expands the space and adds brightness.\nIdeal for offices, education and public areas, corridors, meeting rooms, and refined apartments.\nPairs with white and grey walls, light wood, stainless steel; works well in cool or neutral lighting.\nPractical for daily use: stays neat and cleans easily.\nMood: composed, bright, professionally calm.',
      uz: 'Silver Mist — Gravity’ning yengil talqini: og‘irliksiz “tosh” chuqurligi va silliq o‘tishlar.\nMakonni vizual kengaytiradi va yorqinroq qiladi.\nOfislar, ta’lim va jamoat zonalari, yo‘laklar, muzokara xonalari hamda ozoda kvartiralar uchun mos.\nOq va kulrang devorlar, och yog‘och, zanglamas po‘lat bilan uyg‘un; sovuq/neytral yoritishda yaxshi ishlaydi.\nKundalik amaliy: ozoda ko‘rinishni ushlab turadi, oson tozalanadi.\nKayfiyat: yig‘ilgan, yorug‘, professional sokinlik.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: '/media/SilverMist01.png',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FSilverMist02__4ba1107e.png?alt=media&token=254b85a8-591f-420e-bce7-11981307669c',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FSilverMist03__c84dff0f.png?alt=media&token=2fed9dfa-d493-44ef-9eeb-7ccabdf9c783'
    ]
  },
  {
    id: 'pct-5241',
    name: { ru: 'Ember Stripe', uz: 'Ember Stripe', en: 'Ember Stripe' },
    slug: 'ember-stripe-pct-5241',
    series: 'CARPET TILE',
    collection: 'lines',
    code: 'PCT_5241',
    shortDescription: {
      ru: 'Тёплая графика линий — чтобы собрать пространство и направить поток.',
      en: 'Warm linear graphics to structure the space and guide the flow.',
      uz: 'Makonni tartibga solib, oqimni yo‘naltiruvchi iliq chiziqli grafika.'
    },
    fullDescription: {
      ru: 'Ember Stripe из Lines — линейный рисунок с тёплым оттенком, который добавляет направление и порядок.\nОн визуально «ведёт» пространство: удобно для коридоров, проходов и open-space.\nРекомендуем для офисов, общественных зон, переговорных и коворкингов.\nСочетается с деревом средних тонов, тёплым металлом, камнем; хорошо смотрится при тёплом/нейтральном свете.\nПрактичен: рисунок помогает скрывать мелкие следы, ежедневная уборка проста.\nНастроение: деловая динамика, собранность.',
      en: 'Ember Stripe from Lines features a warm linear pattern that adds direction and order.\nIt visually “guides” the space, making it ideal for corridors, circulation paths, and open-plan areas.\nRecommended for offices, public zones, meeting rooms, and coworking spaces.\nPairs with mid-tone wood, warm metals, and stone; looks great in warm-to-neutral lighting.\nPractical: the pattern helps hide minor marks and daily maintenance is simple.\nMood: business dynamics, structure.',
      uz: 'Lines’dagi Ember Stripe — yo‘nalish va tartib beradigan iliq chiziqli naqsh.\nU makonni vizual “boshlab boradi”: yo‘laklar, o‘tish zonalari va open-space uchun qulay.\nOfislar, jamoat zonalari, muzokara xonalari va kovinglar uchun tavsiya etiladi.\nO‘rtacha tonli yog‘och, iliq metall va tosh bilan uyg‘un; iliq/neytral yoritishda chiroyli ko‘rinadi.\nAmaliy: naqsh mayda izlarni yashiradi, kundalik tozalash oson.\nKayfiyat: ishbilarmon dinamika, tartib.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FEmberStripe01__b4d3e1e8.png?alt=media&token=53056b4a-97a0-4fd9-8542-ed3e90d2e178',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FEmberStripe02__c1d34f5f.png?alt=media&token=841d3222-2731-4e52-ac7a-09746550e88f',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FEmberStripe03__8a01d5c0.png?alt=media&token=9753fa39-ef7c-4eff-a4cf-7087234797ac'
    ]
  },
  {
    id: 'pct-5253',
    name: { ru: 'Cobalt Stripe', uz: 'Cobalt Stripe', en: 'Cobalt Stripe' },
    slug: 'cobalt-stripe-pct-5253',
    series: 'CARPET TILE',
    collection: 'lines',
    code: 'PCT_5253',
    shortDescription: {
      ru: 'Графичный кобальт — акцентный порядок для современных офисов.',
      en: 'Graphic cobalt — an accent of order for modern offices.',
      uz: 'Zamonaviy ofislar uchun tartibli kobalt grafika aksenti.'
    },
    fullDescription: {
      ru: 'Cobalt Stripe — выразительный линейный декор Lines: чёткая графика и ощущение направления.\nПодходит, когда нужно визуально структурировать пространство и подчеркнуть траектории движения.\nИдеален для офисов, коридоров, коворкингов, переговорных и общественных зон.\nСочетается с холодным камнем, стеклом, белыми стенами и чёрной фурнитурой; эффектно при холодном свете.\nЕжедневный уход простой, а рисунок помогает сохранять аккуратный вид.\nНастроение: технологичность, дисциплина, динамика.',
      en: 'Cobalt Stripe is a bold Lines decor: crisp linear graphics with a strong sense of direction.\nUse it to structure the space and highlight circulation paths.\nIdeal for offices, corridors, coworking areas, meeting rooms, and public zones.\nPairs with cool stone, glass, white walls, and black hardware; shines in cooler lighting.\nMaintenance is easy, and the pattern helps keep the surface looking tidy.\nMood: tech, discipline, momentum.',
      uz: 'Cobalt Stripe — Lines’ning kuchli dekori: aniq chiziqli grafika va yo‘nalish hissi.\nMakonni strukturalash va harakat yo‘nalishlarini ta’kidlash uchun qo‘llang.\nOfislar, yo‘laklar, kovinglar, muzokara xonalari va jamoat zonalari uchun mos.\nSovuq tosh, shisha, oq devorlar va qora furnitura bilan uyg‘un; sovuq yoritishda juda ta’sirli.\nParvarishi oson, naqsh esa sirtning ozoda ko‘rinishini saqlashga yordam beradi.\nKayfiyat: texnologiya, intizom, dinamika.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FCobaltStripe01__4a3b1f62.png?alt=media&token=f06c4cef-c8b6-498e-8b84-97832a523aa0',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FCobaltStripe02__965e8a9e.png?alt=media&token=344dd0f9-7737-4ba6-853f-a6b5f901383e',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FCobaltStripe03__e680d63f.png?alt=media&token=f1a2f464-6dea-4a07-9935-95c215aa75e8'
    ]
  },
  {
    id: 'pct-5269',
    name: { ru: 'Shadow Graphite', uz: 'Shadow Graphite', en: 'Shadow Graphite' },
    slug: 'shadow-graphite-pct-5269',
    series: 'CARPET TILE',
    collection: 'lines',
    code: 'PCT_5269',
    shortDescription: {
      ru: 'Графитовая строгость — универсальная база для деловых пространств.',
      en: 'Graphite strictness — a universal base for business interiors.',
      uz: 'Ish interyerlari uchun universal grafit qat’iyligi.'
    },
    fullDescription: {
      ru: 'Shadow Graphite — спокойный, очень собранный декор Lines с лёгкой линейной структурой.\nОн визуально упорядочивает пространство и не отвлекает от мебели и света.\nПодходит для open-space, переговорных, коридоров, офисных лобби и общественных зон.\nСочетается с металлом, бетоном, стеклом и тёмным деревом; стабильно в нейтральном освещении.\nПрактичен: тон и структура помогают сохранять аккуратный вид при ежедневной проходимости.\nНастроение: строгость, порядок, премиальная сдержанность.',
      en: 'Shadow Graphite is a calm, highly composed Lines decor with a subtle linear structure.\nIt visually organizes the space without competing with furniture or lighting.\nGreat for open-plan offices, meeting rooms, corridors, office lobbies, and public areas.\nPairs with metal, concrete, glass, and dark wood; performs best in neutral lighting.\nPractical: the tone and structure help keep a neat look under daily traffic.\nMood: strict, ordered, premium restraint.',
      uz: 'Shadow Graphite — Lines’ning sokin va juda yig‘ilgan dekori, yengil chiziqli strukturaga ega.\nMakonni tartibga soladi, mebel va yoritish bilan raqobat qilmaydi.\nOpen-space, muzokara xonalari, yo‘laklar, ofis hollari va jamoat zonalari uchun mos.\nMetall, beton, shisha va to‘q yog‘och bilan uyg‘un; neytral yoritishda eng yaxshi.\nAmaliy: ton va struktura kundalik yuklamada ham ozoda ko‘rinishni saqlaydi.\nKayfiyat: qat’iylik, tartib, premium soddalik.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FShadowGraphite01__3fee2c01.png?alt=media&token=e4421da6-af73-40bb-b5be-39872f82f63f',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FShadowGraphite02__b6789c83.png?alt=media&token=067ccfeb-42f3-497f-bb8a-628be67dbf97',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FShadowGraphite03__ca92aeee.png?alt=media&token=1421f75e-e1a5-43ad-907b-a3c5e8454057'
    ]
  },
  {
    id: 'pct-5376',
    name: { ru: 'Indigo Haze', uz: 'Indigo Haze', en: 'Indigo Haze' },
    slug: 'indigo-haze-pct-5376',
    series: 'CARPET TILE',
    collection: 'lines',
    code: 'PCT_5376',
    shortDescription: {
      ru: 'Индиго с дымкой — интеллигентный акцент для переговорных и холлов.',
      en: 'Hazy indigo — an intelligent accent for meeting rooms and lobbies.',
      uz: 'Muzokara va hollar uchun muloyim “tumanli” indigo aksenti.'
    },
    fullDescription: {
      ru: 'Indigo Haze в Lines даёт цветовой акцент, но остаётся графичным и строгим благодаря линейной структуре.\nОн помогает зонировать пространство и создавать ощущение направления без агрессии.\nПодходит для переговорных, офисных холлов, зон ожидания, общественных пространств.\nСочетается с серым камнем, светлым деревом и чёрными деталями; отлично под нейтральный свет.\nУход прост: структура маскирует мелкие следы, поддерживая опрятность.\nНастроение: интеллигентный деловой премиум, спокойная динамика.',
      en: 'Indigo Haze adds a color accent while staying graphic and strict thanks to its linear structure.\nIt helps zone the space and create direction without being aggressive.\nGreat for meeting rooms, office lobbies, waiting areas, and public spaces.\nPairs with grey stone, light wood, and black details; best under neutral lighting.\nEasy care: the structure helps mask minor marks and keeps a tidy look.\nMood: intelligent business premium, calm dynamics.',
      uz: 'Indigo Haze chiziqli struktura tufayli rang aksenti beradi, lekin grafika va qat’iylikni saqlaydi.\nMakonni zonalash va yo‘nalish hissini tajovuzsiz yaratishga yordam beradi.\nMuzokara xonalari, ofis hollari, kutish zonalari va jamoat maydonlari uchun mos.\nKulrang tosh, och yog‘och va qora detallar bilan uyg‘un; neytral yoritishda a’lo.\nParvarishi oson: faktura mayda izlarni yashiradi va ozoda ko‘rinishni saqlaydi.\nKayfiyat: ziyrak ishbilarmon premium, sokin dinamika.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FIndigoHaze01__cc355dbe.png?alt=media&token=4d4609c7-bfc7-43f3-b2b7-e48ed2f4441c',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FIndigoHaze02__b666ea8c.png?alt=media&token=d8b0b301-d13b-4e09-a0a4-3b3e921357ee',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FIndigoHaze03__5409a4b6.png?alt=media&token=e7f8a4e2-ca35-4769-9f6e-41892072268d'
    ]
  },
  {
    id: 'pct-6923',
    name: { ru: 'Steel Ash', uz: 'Steel Ash', en: 'Steel Ash' },
    slug: 'steel-ash-pct-6923',
    series: 'CARPET TILE',
    collection: 'lines',
    code: 'PCT_6923',
    shortDescription: {
      ru: 'Стальной пепел — чистая деловая база для активных зон.',
      en: 'Steel ash — a clean business base for high-traffic areas.',
      uz: 'Faol zonalar uchun “po‘lat kul” — toza ishbilarmon baza.'
    },
    fullDescription: {
      ru: 'Steel Ash — нейтральный серый тон Lines с линейной структурой, которая визуально дисциплинирует пространство.\nОн хорошо «держит» большие площади и выглядит профессионально в любой архитектуре.\nРекомендуем для open-space, коридоров, ресепшенов, переговорных и общественных помещений.\nСочетается с бетоном, камнем, стеклом и светлым деревом; стабильно в нейтральном освещении.\nПрактичен: тон и структура помогают сохранять аккуратный вид при ежедневной проходимости.\nНастроение: деловой порядок, спокойная функциональность.',
      en: 'Steel Ash is a neutral grey Lines decor with a linear structure that visually disciplines the space.\nIt holds large areas well and looks professional in any architecture.\nRecommended for open-plan offices, corridors, reception zones, meeting rooms, and public interiors.\nPairs with concrete, stone, glass, and light wood; stable under cool or neutral lighting.\nPractical: the tone and structure help keep a neat look under daily traffic.\nMood: business order, calm functionality.',
      uz: 'Steel Ash — Lines’ning neytral kulrang dekori, chiziqli struktura makonni vizual tartibga soladi.\nKatta maydonlarda yaxshi “ushlab turadi” va har qanday arxitekturaga professional mos tushadi.\nOpen-space, yo‘laklar, resepshen, muzokara xonalari va jamoat interyerlariga tavsiya.\nBeton, tosh, shisha va och yog‘och bilan uyg‘un; sovuq/neytral yoritishda barqaror.\nAmaliy: ton va struktura kundalik oqimda ham ozoda ko‘rinishni saqlaydi.\nKayfiyat: ish tartibi, sokin funksionallik.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FSteelAsh01__14f936a8.png?alt=media&token=71ac87ce-7f6b-43d8-b812-48ad8089f843',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FSteelAsh02__d7bafad3.png?alt=media&token=9c06d9df-5f05-4780-9055-7dbfb7464d4e',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FSteelAsh03__431c6671.png?alt=media&token=9ce44ef6-23fc-4ac8-b78d-1975561a99cb'
    ]
  },
  {
    id: 'pct-6957',
    name: { ru: 'Sage Linen', uz: 'Sage Linen', en: 'Sage Linen' },
    slug: 'sage-linen-pct-6957',
    series: 'CARPET TILE',
    collection: 'lines',
    code: 'PCT_6957',
    shortDescription: {
      ru: 'Мягкий шалфей — спокойный тон для офисов и гостевых зон.',
      en: 'Soft sage — a calm tone for offices and hospitality areas.',
      uz: 'Ofis va mehmon zonalari uchun yumshoq “shalafey” ohangi.'
    },
    fullDescription: {
      ru: 'Sage Linen — деликатный зелёно-серый оттенок Lines с линейной структурой, которая добавляет порядок.\nДекор успокаивает и помогает сделать пространство менее «офисным», но остаётся строгим.\nПодходит для зон ожидания, переговорных, коворкингов, образовательных пространств и дома.\nСочетается с светлым деревом, тёплым камнем и матовым металлом; особенно гармоничен в тёплом свете.\nПрактичен: фактура скрывает мелкие следы, сохраняя аккуратность.\nНастроение: спокойствие, баланс, современный комфорт.',
      en: 'Sage Linen is a delicate green-grey Lines tone with a linear structure that adds order.\nIt calms the interior and softens the “office” feel while staying refined.\nGreat for waiting areas, meeting rooms, coworking, education spaces, and home use.\nPairs with light wood, warm stone, and matte metal; especially harmonious in warm lighting.\nPractical: the texture helps mask small marks and keeps a tidy look.\nMood: calm, balanced, modern comfort.',
      uz: 'Sage Linen — Lines’ning nozik yashil‑kulrang ohangi, chiziqli struktura tartib hissini beradi.\nInteryerni tinchlantiradi, “ofis” kayfiyatini yumshatadi, lekin nafis qoladi.\nKutish zonalari, muzokara xonalari, koving, ta’lim makonlari va uy uchun mos.\nOch yog‘och, iliq tosh va mat metall bilan uyg‘un; iliq yoritishda ayniqsa mos tushadi.\nAmaliy: faktura mayda izlarni yashiradi va ozoda ko‘rinishni saqlaydi.\nKayfiyat: sokinlik, muvozanat, zamonaviy qulaylik.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FSageLinen01__c57799ec.png?alt=media&token=c0d5c7b4-d7f6-4104-9869-8d06058cfd9c',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FSageLinen02__ab4931fd.png?alt=media&token=7c883f42-bc54-49b8-bafd-294f121b8c2a',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FSageLinen03__04d7cef2.png?alt=media&token=ba59507f-78d2-4c97-97a2-cfe0bb854691'
    ]
  },
  {
    id: 'pct-3857',
    name: { ru: 'Sandstone Stripe', uz: 'Sandstone Stripe', en: 'Sandstone Stripe' },
    slug: 'sandstone-stripe-pct-3857',
    series: 'CARPET TILE',
    collection: 'lines',
    code: 'PCT_3857',
    shortDescription: {
      ru: 'Песочный ритм — тёплая структура для коридоров и open‑space.',
      en: 'A sandy rhythm — warm structure for corridors and open-plan areas.',
      uz: 'Yo‘lak va open-space uchun qum rangli iliq ritm va struktura.'
    },
    fullDescription: {
      ru: 'Sandstone Stripe — тёплая линейная графика Lines, которая делает пространство более дружелюбным.\nПолосы задают направление и помогают визуально выстроить маршрут движения.\nИдеально для коридоров, холлов, офисов, торговых зон и общественных пространств.\nСочетается с тёплым деревом, светлым камнем и нейтральными стенами; раскрывается в тёплом свете.\nПрактично: фактура скрывает мелкие следы и сохраняет аккуратность при ежедневной нагрузке.\nНастроение: порядок, лёгкость, спокойная динамика.',
      en: 'Sandstone Stripe is a warm linear Lines pattern that makes interiors feel more welcoming.\nThe stripes add direction and help visually organize circulation.\nPerfect for corridors, lobbies, offices, retail areas, and public interiors.\nPairs with warm wood, light stone, and neutral walls; shines in warm lighting.\nPractical: the texture masks minor marks and stays neat under daily use.\nMood: order, lightness, calm movement.',
      uz: 'Sandstone Stripe — Lines’ning iliq chiziqli naqshi, interyerni yanada mehmondo‘st qiladi.\nChiziqlar yo‘nalish beradi va harakat yo‘llarini vizual tartiblaydi.\nYo‘lak, holl, ofis, savdo zonalari va jamoat interyerlari uchun juda mos.\nIliq yog‘och, och tosh va neytral devorlar bilan uyg‘un; iliq yoritishda chiroyli ochiladi.\nAmaliy: faktura mayda izlarni yashiradi va kundalik foydalanishda ozoda qoladi.\nKayfiyat: tartib, yengillik, sokin dinamika.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FSandstoneStripe01__7bbf2f43.png?alt=media&token=ff25709a-e195-4e55-81ba-15bd811a09a6',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FSandstoneStripe02__29cf9841.png?alt=media&token=3dc06308-6607-4b6e-becd-e50aa720f4ec',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FSandstoneStripe03__716368ce.png?alt=media&token=03c5da65-e4f6-4d53-b227-e7e1d3aa40c0'
    ]
  },
  {
    id: 'pct-3869',
    name: { ru: 'Teal Spark', uz: 'Teal Spark', en: 'Teal Spark' },
    slug: 'teal-spark-pct-3869',
    series: 'CARPET TILE',
    collection: 'lines',
    code: 'PCT_3869',
    shortDescription: {
      ru: 'Бирюзовый импульс — свежий акцент для общественных пространств.',
      en: 'A teal impulse — a fresh accent for public interiors.',
      uz: 'Jamoat interyerlari uchun yangi “biryuza” urg‘u.'
    },
    fullDescription: {
      ru: 'Teal Spark — акцентный декор Lines: линейная графика и свежий цвет, который оживляет пространство.\nОн помогает сделать интерьер более современным и «живым», сохраняя порядок линий.\nПодходит для коворкингов, креативных офисов, переговорных, зон ожидания и коммерции.\nСочетается с светлым деревом, бетоном и чёрными деталями; красиво в нейтральном свете.\nПрактично: рисунок и фактура поддерживают аккуратный вид при ежедневной нагрузке.\nНастроение: динамика, современность, уверенный акцент.',
      en: 'Teal Spark is an accent Lines decor: linear graphics plus a fresh color that energizes the space.\nIt makes interiors feel modern and lively while keeping structure and order.\nGreat for coworking spaces, creative offices, meeting rooms, waiting areas, and commercial zones.\nPairs with light wood, concrete, and black details; looks great in neutral lighting.\nPractical: the texture and pattern keep a neat look under daily traffic.\nMood: dynamic, modern, confidently accented.',
      uz: 'Teal Spark — Lines’ning aksent dekori: chiziqli grafika va makonni jonlantiradigan yangi rang.\nInteryerni zamonaviy va “tirik” qiladi, shu bilan birga chiziqlar tartibini saqlaydi.\nKovinglar, kreativ ofislar, muzokara xonalari, kutish zonalari va tijorat uchun mos.\nOch yog‘och, beton va qora detallar bilan uyg‘un; neytral yoritishda chiroyli.\nAmaliy: faktura va naqsh kundalik yuklamada ham ozoda ko‘rinishni saqlaydi.\nKayfiyat: dinamika, zamonaviylik, ishonchli urg‘u.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FTealSpark01__bc741e30.png?alt=media&token=86ee2cd1-55b2-45a8-a49c-1be5b2c873f0',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FTealSpark02__a9a5bfa0.png?alt=media&token=bc924b24-e7f9-4916-bae8-9162f3d13448',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FTealSpark03__4aaddbb4.png?alt=media&token=50272495-2e6f-4ced-bd08-10d94381116b'
    ]
  },
  {
    id: 'pct-4144',
    name: { ru: 'Copper Weave', uz: 'Copper Weave', en: 'Copper Weave' },
    slug: 'copper-weave-pct-4144',
    series: 'CARPET TILE',
    collection: 'lines',
    code: 'PCT_4144',
    shortDescription: {
      ru: 'Медный штрих — тёплый премиум для лаунжа и переговорных.',
      en: 'A copper touch — warm premium for lounges and meeting rooms.',
      uz: 'Lounge va muzokaralar uchun iliq premium “mis” urg‘u.'
    },
    fullDescription: {
      ru: 'Copper Weave — линейный декор Lines с тёплым медным нюансом и ощущением «плетения».\nОн делает интерьер более дорогим по ощущению, не перегружая графикой.\nПодходит для лаунж-зон, переговорных, ресепшенов, бутик‑коммерции и домашних кабинетов.\nСочетается с тёплым деревом, латунью, камнем и мягким светом; красиво в вечернем освещении.\nПрактичен: фактура сохраняет аккуратность, уход стандартный для ковровой плитки.\nНастроение: тёплый премиум, спокойная уверенность.',
      en: 'Copper Weave is a Lines decor with a warm copper nuance and a subtle “woven” feel.\nIt elevates the interior without overwhelming it with graphics.\nIdeal for lounge zones, meeting rooms, reception areas, boutique commercial spaces, and home offices.\nPairs with warm wood, brass accents, stone, and soft lighting; especially rich in evening light.\nPractical: the texture stays neat and standard carpet-tile care is enough.\nMood: warm premium, calm confidence.',
      uz: 'Copper Weave — Lines’ning iliq mis ohangli, yengil “to‘qima” hissi beruvchi dekori.\nInteryerni qimmatroq ko‘rsatadi, grafika bilan bosib yubormaydi.\nLounge zonalari, muzokara xonalari, resepshen, butik tijorat va uy kabinetlari uchun mos.\nIliq yog‘och, latun aksentlari, tosh va yumshoq yoritish bilan uyg‘un; kechki yoritishda juda boy ko‘rinadi.\nAmaliy: faktura ozoda ko‘rinishni saqlaydi, standart parvarish yetarli.\nKayfiyat: iliq premium, sokin ishonch.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FCopperWeave01__d8320e5c.png?alt=media&token=653236cf-c082-4ee5-9a95-936b378df574',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FCopperWeave02__b12453ff.png?alt=media&token=3dd40536-a7fb-4115-908f-4ad73d35e58c',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FCopperWeave03__b7d39736.png?alt=media&token=32be0a1c-a2a7-4d44-b8ae-49d433f55411'
    ]
  },
  {
    id: 'pct-5236',
    name: { ru: 'Amber Trace', uz: 'Amber Trace', en: 'Amber Trace' },
    slug: 'amber-trace-pct-5236',
    series: 'CARPET TILE',
    collection: 'lines',
    code: 'PCT_5236',
    shortDescription: {
      ru: 'Янтарный след — деликатный тёплый акцент для коммерции и дома.',
      en: 'An amber trace — a delicate warm accent for commercial and home use.',
      uz: 'Tijorat va uy uchun muloyim iliq “yantar” aksenti.'
    },
    fullDescription: {
      ru: 'Amber Trace — тёплый линейный декор Lines, который добавляет свет и направление.\nОн подходит для пространства, где нужен порядок линий и чуть больше «тепла».\nРекомендуем для офисов, коридоров, зон ожидания, переговорных и домашних комнат.\nСочетается с деревом, светлым камнем и тёплыми нейтралями; особенно гармоничен при тёплом свете.\nПрактичен: узор сохраняет аккуратность, уход простой и регулярный.\nНастроение: тёплая деловитость, спокойная динамика.',
      en: 'Amber Trace is a warm Lines decor that adds light and direction through a refined linear pattern.\nUse it when you want structured lines with a bit more warmth.\nRecommended for offices, corridors, waiting zones, meeting rooms, and home interiors.\nPairs with wood, light stone, and warm neutrals; especially harmonious in warm lighting.\nPractical: the pattern keeps a neat look and the care routine is simple.\nMood: warm business tone, calm movement.',
      uz: 'Amber Trace — chiziqli naqsh orqali yorug‘lik va yo‘nalish beradigan iliq Lines dekori.\nChiziqlar tartibi va biroz ko‘proq “iliqlik” kerak bo‘lgan joylarda mos.\nOfislar, yo‘laklar, kutish zonalari, muzokara xonalari va uy interyerlariga tavsiya.\nYog‘och, och tosh va iliq neytrallar bilan uyg‘un; iliq yoritishda ayniqsa mos tushadi.\nAmaliy: naqsh ozoda ko‘rinishni saqlaydi, parvarish oddiy va muntazam.\nKayfiyat: iliq ishbilarmonlik, sokin dinamika.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FAmberTrace01__bc6e29ea.png?alt=media&token=e3378542-a23f-4ec7-aac8-c8225b73b0af',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FAmberTrace02__277a3ac6.png?alt=media&token=496a45f4-5273-4c1e-b0fe-3d2632b657b6',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FAmberTrace03__4dfb6a82.png?alt=media&token=4769e8e5-fc82-4169-978f-9ebb20686c49'
    ]
  },
  {
    id: 'pct-5276',
    name: { ru: 'Arctic Current', uz: 'Arctic Current', en: 'Arctic Current' },
    slug: 'arctic-current-pct-5276',
    series: 'CARPET TILE',
    collection: 'lines',
    code: 'PCT_5276',
    shortDescription: {
      ru: 'Холодный поток — светлая графика для чистых офисов и коридоров.',
      en: 'A cool current — light graphics for clean offices and corridors.',
      uz: 'Toza ofislar va yo‘laklar uchun sovuq “oqim” va yengil grafika.'
    },
    fullDescription: {
      ru: 'Arctic Current — светлый, «холодный» декор Lines с направляющей линейной структурой.\nОн делает пространство визуально чище и шире, добавляя организованность.\nПодходит для коридоров, офисов, общественных зон, учебных пространств и минималистичных интерьеров.\nСочетается с белыми стенами, стеклом, холодным камнем и светлым деревом; лучше всего при холодном/нейтральном свете.\nПрактично: структура помогает поддерживать аккуратный вид при ежедневной эксплуатации.\nНастроение: свежесть, порядок, современная строгость.',
      en: 'Arctic Current is a light, cool Lines decor with a directional linear structure.\nIt makes interiors feel cleaner and more spacious while adding organization.\nGreat for corridors, offices, public zones, education spaces, and minimalist interiors.\nPairs with white walls, glass, cool stone, and light wood; best under cool-to-neutral lighting.\nPractical: the texture supports a tidy look in everyday use.\nMood: fresh, ordered, modern strictness.',
      uz: 'Arctic Current — yo‘nalish beruvchi chiziqli struktura ega och va sovuq Lines dekori.\nInteryerni yanada “toza” va keng ko‘rsatadi, tartib qo‘shadi.\nYo‘laklar, ofislar, jamoat zonalari, ta’lim makonlari va minimalistik interyerlar uchun mos.\nOq devorlar, shisha, sovuq tosh va och yog‘och bilan uyg‘un; sovuq/neytral yoritishda eng yaxshi.\nAmaliy: faktura kundalik foydalanishda ham ozoda ko‘rinishni qo‘llab-quvvatlaydi.\nKayfiyat: yangilik, tartib, zamonaviy qat’iyat.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FArcticCurrent01__129f5227.png?alt=media&token=6d7c3e28-0842-4575-b873-63aaca973bf8',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FArcticCurrent02__aeb19f87.png?alt=media&token=9e0ca6c8-927d-487a-90ce-24a5f3bd7777',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FArcticCurrent03__d32c5902.png?alt=media&token=f316b350-28a2-4564-abc2-a52943b6aa93'
    ]
  },
  {
    id: 'pct-3644',
    name: { ru: 'Umber Brassline', uz: 'Umber Brassline', en: 'Umber Brassline' },
    slug: 'umber-brassline-pct-3644',
    series: 'CARPET TILE',
    collection: 'lines',
    code: 'PCT_3644',
    shortDescription: {
      ru: 'Тёмный умбр — премиальный ритм для кабинетов и представительских зон.',
      en: 'Deep umber — a premium rhythm for executive and reception areas.',
      uz: 'Kabinet va vakillik zonalari uchun premium to‘q umbra ritmi.'
    },
    fullDescription: {
      ru: 'Umber Brassline — глубокий тёплый тон Lines с линейной структурой и благородной подачей.\nОн собирает интерьер и добавляет статус без лишнего контраста.\nПодходит для кабинетов, переговорных, ресепшенов, лаунж-зон и коммерческих холлов.\nСочетается с тёплым деревом, камнем, латунью и тёплым светом; выглядит особенно «дорого» вечером.\nПрактичен: тёмная база и структура помогают сохранять аккуратность в ежедневной эксплуатации.\nНастроение: премиальная сдержанность, уверенная деловитость.',
      en: 'Umber Brassline is a deep warm Lines tone with a refined linear structure and a noble presence.\nIt brings status and cohesion without harsh contrast.\nIdeal for executive offices, meeting rooms, reception areas, lounges, and commercial lobbies.\nPairs with warm wood, stone, brass accents, and warm lighting; feels especially premium in the evening.\nPractical: the dark base and structure help maintain a neat look day to day.\nMood: premium restraint, confident business tone.',
      uz: 'Umber Brassline — nafis chiziqli struktura va olijanob ko‘rinishga ega chuqur iliq Lines dekori.\nKeskin kontrastsiz ham status va uyg‘unlik beradi.\nKabinetlar, muzokara xonalari, resepshen, lounge va tijorat hollari uchun mos.\nIliq yog‘och, tosh, latun aksentlari va iliq yoritish bilan uyg‘un; kechqurun ayniqsa premium ko‘rinadi.\nAmaliy: to‘q baza va struktura kundalik foydalanishda ozoda ko‘rinishni saqlaydi.\nKayfiyat: premium soddalik, ishonchli ishbilarmonlik.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FUmberBrassline01__2b9ab71f.png?alt=media&token=0a18ce59-dbb2-4ffc-bf36-4584023cd845',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FUmberBrassline02__54cc4cf7.png?alt=media&token=815a1b51-3b43-4a3d-9507-916d94298c30',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FUmberBrassline03__482e1fcd.png?alt=media&token=28e8b9b5-a1aa-4232-b6be-138c7b7e264d'
    ]
  },
  {
    id: 'pct-3844',
    name: { ru: 'Slate Silverline', uz: 'Slate Silverline', en: 'Slate Silverline' },
    slug: 'slate-silverline-pct-3844',
    series: 'CARPET TILE',
    collection: 'lines',
    code: 'PCT_3844',
    shortDescription: {
      ru: 'Сланцевый серый с линией — собранная графика для офисов и холлов.',
      en: 'Slate grey with a line — composed graphics for offices and lobbies.',
      uz: 'Ofis va hollar uchun chiziqli slanets kulrang — tartibli grafika.'
    },
    fullDescription: {
      ru: 'Slate Silverline — строгое сочетание сланцевого серого и линейной структуры Lines.\nДекор визуально структурирует пространство и добавляет ощущение направления.\nПодходит для офисных коридоров, open-space, переговорных, ресепшенов и общественных зон.\nСочетается с бетоном, камнем, стеклом и металлом; стабильно в нейтральном освещении.\nПрактичен: тон и структура помогают сохранять аккуратный вид при ежедневной проходимости.\nНастроение: деловая собранность, современная строгость.',
      en: 'Slate Silverline is a strict blend of slate grey and the linear Lines structure.\nIt visually structures the space and adds a sense of direction.\nIdeal for office corridors, open-plan areas, meeting rooms, reception zones, and public interiors.\nPairs with concrete, stone, glass, and metal; stable in neutral lighting.\nPractical: the tone and structure help keep a neat appearance under daily traffic.\nMood: business composure, modern strictness.',
      uz: 'Slate Silverline — slanets kulrang va Lines’ning chiziqli strukturasini qat’iy uyg‘unligi.\nMakonni vizual strukturalaydi va yo‘nalish hissini beradi.\nOfis yo‘laklari, open-space, muzokara xonalari, resepshen va jamoat interyerlariga mos.\nBeton, tosh, shisha va metall bilan uyg‘un; neytral yoritishda barqaror.\nAmaliy: ton va struktura kundalik oqimda ham ozoda ko‘rinishni saqlaydi.\nKayfiyat: ishbilarmon yig‘ilganlik, zamonaviy qat’iyat.'
    },
    specs: { totalHeight: '6,2 мм', size: '50×50 см', pileHeight: '3 мм' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FSlateSilverline01__13f25d91.png?alt=media&token=ebf2aa52-853b-4fbf-886e-5a3c7c8cb0ad',
    images: [
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FSlateSilverline02__dbf20ac3.png?alt=media&token=9c5614a9-32db-4e02-8f4b-7957aa060d71',
      'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FtxG59yUzRMeZtecoq8fqm04OeyD2%2FSlateSilverline03__fbbc2f6f.png?alt=media&token=a46db6dc-cdae-42f1-b1f8-2e70a6f6ae09'
    ]
  },
  // ACCESSORIES - 4 Products
  {
    id: 'acc-underlay-ixpe',
    name: {
      ru: 'Подложка IXPE',
      en: 'IXPE Underlay',
      uz: 'IXPE taglik'
    },
    slug: 'ixpe-underlay',
    series: 'ACCESSORIES',
    code: 'ACC-001',
    compatibility: ['SPC LAMINATE'],
    shortDescription: {
      ru: 'Тихий шаг и ровная геометрия замка',
      en: 'Quieter steps, better lock support',
      uz: 'Yumshoq qadam va qulfni qo‘llab-quvvatlash'
    },
    fullDescription: {
      ru: 'Профессиональная подложка IXPE специально разработана для использования под SPC покрытия. Она обеспечивает значительное снижение ударного шума, компенсирует микронеровности основания и создает стабильную опору для замковой системы, продлевая срок службы пола.',
      en: 'Professional IXPE underlay is specially designed for use under SPC flooring. It provides significant reduction in impact noise, compensates for micro-irregularities of the base, and creates stable support for the locking system, extending the life of the floor.',
      uz: 'Professional IXPE taglik maxsus SPC qoplamalari ostida foydalanish uchun ishlab chiqilgan. U zarba shovqinini sezilarli darajada kamaytirishni ta’minlaydi, asosning mikro-notekisliklarini kompensatsiya qiladi va qulflash tizimi uchun barqaror tayanch yaratadi, polning xizmat qilish muddatini uzaytiradi.'
    },
    specs: { thickness: '1.5 mm', material: 'IXPE', density: 'high' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FludOJwATJSVlW062ubN48fVuR6D3%2Fimage__8b3bad5d.png?alt=media&token=43e1efa7-d11b-4322-a02b-a4f325b3d1d7',
    images: []
  },
  {
    id: 'acc-skirting',
    name: {
      ru: 'Плинтус',
      en: 'Skirting Board',
      uz: 'Plintus'
    },
    slug: 'skirting-board',
    series: 'ACCESSORIES',
    code: 'ACC-002',
    compatibility: ['SPC LAMINATE', 'SPC PANELS'],
    shortDescription: {
      ru: 'Аккуратное примыкание и защита стены',
      en: 'Neat joint and wall protection',
      uz: 'Devorning toza birlashishi va himoyasi'
    },
    fullDescription: {
      ru: 'Плинтус обеспечивает эстетичное завершение отделки пола или стен. Он скрывает технологические зазоры, защищает нижнюю часть стены от загрязнений и повреждений при уборке, а также позволяет проложить кабели внутри.',
      en: 'The skirting board provides an aesthetic finish to the floor or wall. It hides technological gaps, protects the lower part of the wall from dirt and damage during cleaning, and allows for cable management inside.',
      uz: 'Plintus pol yoki devor qoplamasining estetik yakunini ta’minlaydi. U texnologik bo‘shliqlarni yashiradi, tozalash vaqtida devorning pastki qismini ifloslanish va shikastlanishdan himoya qiladi, shuningdek, ichkarida kabellarni o‘tkazishga imkon beradi.'
    },
    specs: { height: '60 mm', length: '2400 mm', material: 'PVC/SPC' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FludOJwATJSVlW062ubN48fVuR6D3%2F2025__17859792.webp?alt=media&token=1241e082-6fdd-4ae4-9f68-bfbbb94ce019',
    images: []
  },
  {
    id: 'acc-sealant',
    name: {
      ru: 'Герметичный клей',
      en: 'Sealant / Adhesive',
      uz: 'Germetik yelim'
    },
    slug: 'sealant-adhesive',
    series: 'ACCESSORIES',
    code: 'ACC-003',
    compatibility: ['SPC LAMINATE', 'SPC PANELS'],
    shortDescription: {
      ru: 'Герметизация стыков в мокрых зонах',
      en: 'Sealing joints in wet areas',
      uz: 'Nam zonalarda choklarni germetizatsiya qilish'
    },
    fullDescription: {
      ru: 'Специализированный герметик для фиксации и герметизации стыков панелей и ламината. Обеспечивает надежную защиту от проникновения влаги, создавая аккуратный и долговечный шов.',
      en: 'Specialized sealant for fixing and sealing joints of panels and laminate. Provides reliable protection against moisture penetration, creating a neat and durable joint.',
      uz: 'Panellar va laminat tutashgan joylarini mahkamlash va germetizatsiya qilish uchun ixtisoslashtirilgan germetik. Namlik kirishidan ishonchli himoya qiladi, toza va chidamli chok hosil qiladi.'
    },
    specs: { volume: '310 ml', color: 'Transparent', type: 'Hybrid' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FludOJwATJSVlW062ubN48fVuR6D3%2F2020__c0711aab.webp?alt=media&token=3e14b727-1a24-487f-a947-a67aebbb5ef5',
    images: []
  },
  {
    id: 'acc-transition-profile',
    name: {
      ru: 'Переходной профиль (порожек)',
      en: 'Transition Profile (T-moulding)',
      uz: 'O‘tish profili (T-profil)'
    },
    slug: 'transition-profile',
    series: 'ACCESSORIES',
    code: 'ACC-004',
    compatibility: ['SPC LAMINATE'],
    shortDescription: {
      ru: 'Аккуратный стык покрытий без перепада',
      en: 'Clean joint between floorings',
      uz: 'Qoplamalar tutashuvi uchun'
    },
    fullDescription: {
      ru: 'Алюминиевый переходной профиль предназначен для эстетичного оформления стыка между различными типами напольных покрытий или в дверных проемах. Он закрывает технологический зазор и защищает края покрытий от сколов.',
      en: 'The aluminum transition profile is designed for the aesthetic design of the joint between different types of flooring or in doorways. It covers the technological gap and protects the edges of the coatings from chips.',
      uz: 'Alyuminiy o‘tish profili turli xil pol qoplamalari o‘rtasidagi yoki eshik o‘rinlaridagi tutashgan joylarni estetik jihatdan bezash uchun mo‘ljallangan. U texnologik bo‘shliqni yopadi va qoplamalar chetlarini sinishdan himoya qiladi.'
    },
    specs: { material: 'Aluminum', length: '900 mm', installation: 'Hidden' },
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FludOJwATJSVlW062ubN48fVuR6D3%2F2023__9d5736be.webp?alt=media&token=9c11d1ae-57f3-4ee0-8f53-5e00ed00d5eb',
    images: []
  }
];
